// ===== Business Document Chat Card (BD-C1 Phase 1: 카드 셸 / Phase 2: 액션 스트립) =====
// 대화방 아티팩트 카드(.artifact-card) 안에 문서 상태 패널(.bdc-panel)을 부착한다.
// chat.ts 의 renderArtifactCard 가 `typeof BusinessDocumentCard !== "undefined"` 가드 한 줄로
// 호출한다 — 이 모듈이 없어도 아티팩트 카드는 그대로 그려진다.
//
// classic script 전역이다. import/export 를 한 줄도 두지 않는다 —
// tsconfig.frontend.json 이 module: "None" 이라 모듈 키워드가 하나라도 있으면 이 파일이 TS 모듈이
// 되고, 그 순간 chat.ts 의 BusinessDocumentCard 참조가 TS2304 로 typecheck 를 깨뜨린다.
// 선례: work-inline-cards.ts, views/business-documents/inbox.ts (둘 다 import/export 0줄).
//
// renderMarkdown 을 경유하지 않는다(chat.ts:805-807 과 같은 이유) — DOMPurify SANITIZE_CONFIG 가
// data-* 를 스트립해 data-business-document-id 가 소실된다. 구조화 레코드 → 필드 단위 textContent
// → 직접 DOM 조립만 쓴다.

interface BusinessDocumentCardMetaRow {
  label: string;
  value: string;
  unassigned: string;
}

interface BusinessDocumentCardView {
  stateKey: string;
  stateLabel: string;
  rows: BusinessDocumentCardMetaRow[];
  message: string;
  isDraft: boolean;
  actions: string[];
  primaryAction: string;
  notice: string;
  downloadHref: string;
  // 규범 필수란 중 아직 빈 경로 수. null = 미상(조회 실패) 또는 비대상 상태(제출 이후).
  missingCount: number | null;
  // 반려 사유(record.rejection.reason). 작성 단계에서만 그린다 — 재제출이 rejection 을 지우므로
  // 이 값의 존재가 곧 "반려 후 아직 재제출 전" 이다.
  rejectionReason: string;
}

// 아티팩트 payload 의 **발행 시점 스냅샷**이다. 문서의 현재 상태와 별개이며, 이 카드가 가리키는
// PDF 바이트가 무엇인지를 말한다. 패널 속성에 실어두는 이유는 조회 응답과 짝지어 읽어야
// canDownload 세 조건을 판정할 수 있기 때문이다.
interface BusinessDocumentCardSnapshot {
  isDraft: boolean;
  documentNumber: string;
  path: string;
}

// 정식 발행본 산출물의 URL 한 쌍. 문서함 상세가 이것을 링크로 건다.
interface BusinessDocumentPublishedOutput {
  pdfHref: string;
  htmlHref: string;
}

// 인테이크 폼 한 벌의 DOM 좌표. 제출 핸들러가 클로저로 값을 들고 다니지 않고 이 레코드를 통해
// 칸을 다시 읽는다 — 사용자가 고친 뒤의 값이 제출 값이어야 하기 때문이다.
interface BusinessDocumentIntakeState {
  container: HTMLElement;
  documentType: string;
  titleInput: HTMLInputElement;
  counterpartyInput: HTMLInputElement;
  error: HTMLElement;
  submit: HTMLButtonElement;
}

var BusinessDocumentCard = (function () {
  // ===== 분류자 =====
  // lib/tools/business-documents/publish-ops.ts:65 BUSINESS_DOCUMENT_ARTIFACT_KIND_BY_TYPE 의
  // 값 역맵이다. 브라우저 classic script 는 서버 모듈을 require 할 수 없어 사본이 불가피하므로,
  // phase-01 검증블록의 드리프트 게이트가 두 표를 **값으로** 대조한다.
  var _DOCUMENT_TYPE_BY_ARTIFACT_KIND: Record<string, string> = {
    "business-document-quotation": "quotation",
    "business-document-contract": "contract",
  };

  // ===== 어휘 =====
  // views/business-documents/inbox.ts:86-93 _STATE_LABELS 의 사본이다(BUSINESS_DOCUMENT_STATES
  // 6종). 같은 사실을 두 표면이 다른 말로 부르면 사용자는 다른 일이라고 믿는다 —
  // test/business-document-chat-card.test.ts 가 두 소스를 읽어 값 동등을 고정한다.
  var _STATE_LABELS: Record<string, string> = {
    draft: "초안",
    needs_input: "입력 필요",
    review_pending: "승인 대기",
    approved: "승인됨",
    rendered: "발행됨",
    void: "무효",
  };

  // inbox.ts:100-105 _NUMBER_KEY_BY_TYPE 사본 — 문서번호는 봉투가 아니라 본문 키에 있다.
  var _NUMBER_KEY_BY_TYPE: Record<string, string> = {
    quotation: "documentNumber",
    contract: "contractNumber",
  };

  // lib/tools/business-documents/publish-ops.ts OUTPUT_URL_PREFIX 사본. 산출물은 정적이 아니라
  // /api 전역 인증 가드 아래의 BD 전용 라우트가 스트리밍한다(routes-render.ts GET /files/:name —
  // 파일 보호 트랙이 /outputs 직접 서빙을 닫은 뒤의 유일한 창구다). 값 동등은 드리프트 게이트가 고정.
  var _OUTPUT_URL_PREFIX = "/api/business-documents/files";

  // lib/business-documents/contracts.ts BUSINESS_DOCUMENT_ID_RE 사본. 소비자가 이 id 로 href 를
  // 만들므로 슬러그 규칙 밖 값은 URL 을 만들지 않는다.
  var _DOCUMENT_ID_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

  var _EMPTY_VALUE = "—";

  // inbox.ts _UNASSIGNED_NUMBER 사본. 번호 부재의 의미는 상태마다 다르다 —
  // expected(설계된 정상) / pending(채번 대기) / anomaly(발행 사고)를 한 표기로 접지 않는다.
  // 문구는 전 상태 "발행 시 채번" 으로 통일했다(2026-08-30) — 실제 채번은 발행 전이에서
  // 일어나므로(publish-ops/numbering) "승인 후 채번" 은 한 걸음 이른 거짓 약속이었다.
  var _UNASSIGNED_NUMBER: Record<string, { kind: string; text: string }> = {
    draft: { kind: "expected", text: "발행 시 채번" },
    needs_input: { kind: "expected", text: "발행 시 채번" },
    review_pending: { kind: "expected", text: "발행 시 채번" },
    approved: { kind: "pending", text: "발행 시 채번" },
    rendered: { kind: "anomaly", text: "번호 없음" },
    void: { kind: "expected", text: _EMPTY_VALUE },
  };

  // 6개 밖의 상태(도달 불가 방어)는 판단을 유보한다 — 정상도 사고도 주장하지 않는다.
  var _UNASSIGNED_FALLBACK = { kind: "expected", text: _EMPTY_VALUE };

  var _LABEL_NUMBER = "문서번호";
  var _LABEL_COUNTERPARTY = "상대방";

  var _PILL_LOADING = "불러오는 중";
  var _PILL_ERROR = "불러오기 실패";

  var _DRAFT_BADGE = "DRAFT";
  var _MSG_DRAFT_WARNING = "초안 미리보기입니다. 승인·발행 전에는 외부에 공유하지 마세요.";

  // 실패 원인별 문구. 하나로 접으면 "지워진 문서"와 "잠깐 끊긴 네트워크"가 같은 말이 되어
  // 사용자가 어느 쪽인지 추적할 수 없다.
  var _MSG_NOT_FOUND = "문서를 찾을 수 없습니다. 삭제되었거나 이동했을 수 있습니다.";
  var _MSG_UNAUTHORIZED = "문서를 볼 권한이 없습니다. 로그인 상태를 확인하세요.";
  var _MSG_BAD_REQUEST = "문서 데이터에 문제가 있습니다. 관리자 확인이 필요합니다.";
  var _MSG_FETCH_FAILED = "문서 상태를 불러오지 못했습니다.";

  // ===== 상태 -> 액션 (유일한 판정자) =====
  // 이 표가 액션 게이팅의 **단 하나의** 판정자다. 버튼 렌더 코드는 상태를 두 번째로 보지
  // 않는다 — 두 판정자가 생기는 순간 한쪽만 갱신되는 드리프트가 곧바로 "프런트가 숨긴 버튼을
  // 서버는 허용" 이 된다.
  //
  // edit 가 붙는 상태 집합은 서버의 BUSINESS_DOCUMENT_BODY_EDITABLE_STATES(contracts.ts:429,
  // = {draft, needs_input})와 값으로 같아야 한다. 그 외 상태에서 PATCH /:id/body 는
  // body_not_editable(409)로 거부된다 — 프런트가 여는 칸과 서버가 받는 칸이 갈라지면 사용자는
  // 저장 버튼을 누른 뒤에야 그것을 안다. phase-03 검증 게이트가 이 표를 **함수 호출로** 뽑아
  // 서버 상수와 대조한다(문자열 존재 검사는 Phase 1 이 이미 상태 라벨을 심어 두어 공허하다).
  //
  // void 행이 빈 것은 void 가 나가는 전이 없는 종착 상태이기 때문이다(contracts.ts
  // BUSINESS_DOCUMENT_TRANSITIONS — 폐기된 문서는 어떤 액션도 받지 않는다).
  //
  // approved 의 void 는 갇힌 문서 탈출구다(웨이브4 인계 §3-1 (c) — 미완성 본문으로 승인되면
  // 발행도 수정도 불가한 막다른 길). rendered→void 전이도 맵에는 있지만 발행본 폐기 노출은
  // 별도 제품 판단이라 여기 열지 않는다.
  var _ACTIONS_BY_STATE: Record<string, string[]> = {
    draft: ["preview", "edit", "submit"],
    needs_input: ["preview", "edit", "submit"],
    review_pending: ["preview", "approve", "reject"],
    approved: ["publish", "void"],
    rendered: ["download"],
    void: [],
  };

  // 강조(단일 액센트)만 고르는 표시용 표다. **게이팅에 관여하지 않는다** — 여기 무엇이 적혀
  // 있어도 액션 집합은 위 표만이 정한다. 값이 해당 상태의 액션 집합 안에 있는지는
  // business-document-chat-card-actions.test.ts 가 고정한다.
  var _PRIMARY_ACTION_BY_STATE: Record<string, string> = {
    draft: "submit",
    // needs_input 의 의미는 "본문이 덜 채워졌다" 그 자체다(routes-body.ts:163-172). 이 상태에서
    // 승인 요청을 강조하면 같은 빈 본문이 다시 review_pending 으로 올라가 다시 반려되는 고리를
    // 액센트가 가리킨다 — 다음에 통제할 수 있는 행동은 수정이다(design tokens §Signature).
    needs_input: "edit",
    review_pending: "approve",
    approved: "publish",
    rendered: "download",
    void: "",
  };

  var _ACTION_LABELS: Record<string, string> = {
    preview: "미리보기",
    edit: "수정",
    submit: "승인 요청",
    approve: "승인",
    reject: "반려",
    publish: "발행",
    download: "다운로드",
    void: "폐기",
  };

  // 미완성 문서의 edit 라벨(사용자 결정 2026-08-30) — 아직 쓴 게 없는 문서에 "수정" 은
  // 이미 있는 것을 고친다는 거짓 전제를 심는다. 완성도를 아는 미완성 상태에서만 이 라벨을 쓴다.
  var _ACTION_LABEL_EDIT_EMPTY = "내용 입력";

  // 반려 표시(P-반려, 사용자 승인 라운드2). 반려 폼이 "작성자에게 전달되고" 라고 약속하므로
  // 작성자의 표면(카드)이 사유를 실제로 그린다 — 문서함까지 가야 보이던 것을 끌어온다.
  var _MSG_REJECTED_TITLE = "반려됨 — 검토자가 돌려보냈어요";

  // ===== 스트립 표시 문법 (UX 개편 P1, 사용자 승인 2026-08-29) =====
  // 전진 액션(액센트 1개)은 항상 오른쪽 끝, 보조·후퇴 액션은 왼쪽에 파이프라인 순서로.
  // 이 표는 **표시 순서만** 정한다 — 액션 집합은 _ACTIONS_BY_STATE 만이 정하고, 강조 선택은
  // _PRIMARY_ACTION_BY_STATE(+ 완성도 override)가 정한다. 세 표가 각자 한 가지만 맡는다.
  var _ACTION_DISPLAY_RANK: Record<string, number> = {
    edit: 1,
    preview: 2,
    reject: 3,
    void: 4,
    submit: 5,
    approve: 6,
    publish: 7,
    download: 8,
  };

  // 후퇴·파기 계열은 조용한 위험 톤 — 오른쪽 끝의 전진 액센트와 시각적으로 분리해
  // 승인·반려 오클릭 인접성을 없앤다(P1).
  var _DANGER_ACTIONS = ["reject", "void"];

  // ===== 여정 스테퍼 (P2) =====
  // 정상 경로 4걸음. void 는 경로 밖 종착이라 pill 폴백으로 남는다 — 없는 걸음을 발명하지
  // 않는다. 미상 상태·로딩·실패도 pill 이다(_renderStatusOnly 는 stateKey "" 로 온다).
  var _JOURNEY_STEPS: { key: string; label: string }[] = [
    { key: "draft", label: "초안" },
    { key: "review_pending", label: "승인 대기" },
    { key: "approved", label: "승인" },
    { key: "rendered", label: "발행" },
  ];

  // needs_input 은 여정상 초안 자리다 — 반려·미완성 모두 "아직 작성 단계" 라는 같은 사실이다.
  // 현재 걸음의 라벨은 _STATE_LABELS 를 그대로 쓰므로 "입력 필요" 라는 어휘는 사라지지 않는다.
  var _JOURNEY_INDEX: Record<string, number> = {
    draft: 0,
    needs_input: 0,
    review_pending: 1,
    approved: 2,
    rendered: 3,
  };

  // ===== 완성도 카운터 (P3) =====
  var _MSG_MISSING_COUNT_PREFIX = "아직 비어 있는 필수 항목 ";
  var _MSG_MISSING_COUNT_SUFFIX = "칸";
  var _MSG_BODY_COMPLETE = "필수 항목이 모두 채워졌어요 — 미리보기로 확인한 뒤 승인을 요청하세요.";

  // ===== 오류 문구 =====
  // 키 집합은 lib/tools/business-documents/http-errors.ts ERROR_STATUS 17개 + 폴백
  // internal_error(classifyError 의 마지막 return)다. 서버가 코드를 늘리면
  // business-document-chat-card-actions.test.ts 가 그 모듈을 require 해 누락을 잡는다.
  //
  // 서버는 원문 메시지를 주지 않는다(http-errors.ts 머리 주석 — 발행자 secrets 의 절대경로가
  // 메시지에 실리므로 응답은 코드만, 원문은 서버 로그만). 그래서 코드 -> 문구는 프런트 소유다.
  var _ERROR_COPY: Record<string, string> = {
    "invalid_body": "요청 형식이 올바르지 않아 처리하지 못했어요.",
    "invalid_document_id": "문서 주소가 올바르지 않아요.",
    "not_authenticated": "다시 로그인해 주세요.",
    "room_membership_required": "이 문서가 나온 방의 구성원만 볼 수 있어요.",
    "document_not_found": "문서를 찾을 수 없어요.",
    "document_already_exists": "같은 문서가 이미 있어요.",
    "invalid_transition": "문서 상태가 바뀌었어요.",
    // 이 코드는 서로 다른 사실 다섯 개가 접힌 것이라(§4) 아래 _explainRenderPrecondition 이
    // 두 갈래로 풀어낸다. 이 문구는 그 두 갈래가 모두 실패했을 때의 폴백이다.
    "render_preconditions_unmet": "지금 상태에서는 문서를 만들 수 없어요.",
    "already_approved": "이미 승인된 문서예요.",
    "stale_rejection": "이전 반려 기록이 남아 있어 처리하지 못했어요.",
    "body_not_editable": "지금 상태에서는 본문을 고칠 수 없어요.",
    "rejection_history_immutable": "이 문서의 반려 이력은 지울 수 없어요.",
    "rejection_outcome_ambiguous": "반려 처리 결과를 확인하지 못했어요. 문서 상태를 새로고침한 뒤 다시 시도해 주세요.",
    "approval_outcome_ambiguous": "승인 처리 결과를 확인하지 못했어요. 문서 상태를 새로고침한 뒤 다시 시도해 주세요.",
    // 렌더 경로 미도달. rethrowAsPrecondition(business-document-model.ts:159-162)이 발행 법인
    // 실패를 RenderPreconditionError 로 다시 던지므로 http-errors.ts:79-81 의 접두 매칭에 걸리지
    // 않는다. 이 카드의 다섯 액션은 이 코드를 받지 못하지만 **초안 생성**(POST /)은 받는다 —
    // document-store.create 의 fail-closed assertIssuerReadyForDocument 가 도달하는 유일한
    // 경로가 인테이크다(phase-04 §3). 그래서 문구가 다음 행동까지 말한다.
    "issuer_not_ready": "발행 법인 설정이 아직 준비되지 않았어요 — 관리자 확인이 필요합니다.",
    "runtime_dep_missing": "서버 구성이 준비되지 않았어요. 관리자 확인이 필요합니다.",
    "room_store_disabled": "방 정보를 확인할 수 없어 처리하지 못했어요.",
    "internal_error": "처리 중 문제가 생겼어요. 잠시 후 다시 시도해 주세요.",
  };

  // 서버 코드가 아니라 클라이언트 쪽 실패(네트워크·JSON 파싱)다. 서버 코드 표에 섞으면
  // ERROR_STATUS 유도 관계가 흐려지므로 별도로 둔다.
  var _MSG_ACTION_NETWORK = "요청을 보내지 못했습니다. 연결을 확인해 주세요.";

  var _MSG_INCOMPLETE = "아직 채워지지 않은 항목이 있어 미리보기를 만들 수 없어요.";
  var _MSG_ALREADY_RENDERED = "이미 발행된 문서입니다.";
  var _MSG_VOIDED = "폐기된 문서입니다.";
  var _MSG_ISSUER_OR_AMOUNT = "발행 법인 설정 또는 저장된 금액에 문제가 있어 만들 수 없어요 — 관리자 확인이 필요합니다.";

  // canDownload 세 조건 중 하나라도 어긋났을 때. 갈래는 넷이다: 스냅샷이 초안 / path 가 빔
  // (독립 카드) / 번호 불일치 / 미지 문서 종류(numberKey 없음). 마지막 갈래에서는 문서함도
  // 링크를 만들지 못하므로(같은 판정자를 쓴다) "발행본은 최신 카드에 있다" 는 네 갈래 전부에서
  // 참이 아니다. 없는 표면을 약속하지 않고 문서함으로 안내만 한다.
  var _MSG_DRAFT_SNAPSHOT = "이 카드는 발행본 PDF 를 가리키지 않습니다 — 문서함에서 최신 상태와 발행본을 확인하세요.";

  // ===== 반려 미니폼 (P5) =====
  // window.prompt 를 쓰지 않는다 — 한 줄 입력이라 사유를 제대로 못 쓰고, 문서 맥락이 가려지고,
  // 이력에 영속되는 값(웨이브4 T1)을 받는 입력치고 가장 약한 표면이었다. 본문 폼과 같은
  // 관용구(카드 안 인라인)로 통일한다.
  var _REJECT_FORM_TITLE = "반려 사유 — 작성자에게 전달되고 이력에 남습니다";
  var _REJECT_CONFIRM_LABEL = "반려 확정";
  var _REJECT_BACK_LABEL = "돌아가기";
  var _MSG_REJECT_REASON_REQUIRED = "반려 사유를 입력해 주세요.";

  var _CONFIRM_PUBLISH_TITLE = "문서를 발행할까요?";
  var _CONFIRM_PUBLISH_BODY = "발행하면 문서번호가 확정되고 정식본 PDF가 만들어집니다.";
  var _CONFIRM_PUBLISH_WARNING = "발행은 되돌릴 수 없습니다.";
  var _CONFIRM_PUBLISH_LABEL = "발행";
  var _CONFIRM_VOID_TITLE = "문서를 폐기할까요?";
  var _CONFIRM_VOID_BODY = "폐기하면 이 문서로는 더 이상 수정도 발행도 할 수 없습니다.";
  var _CONFIRM_VOID_WARNING = "폐기는 되돌릴 수 없습니다.";
  var _CONFIRM_VOID_LABEL = "폐기";

  // ===== 인테이크 어휘·상수 (BD-C1 Phase 4) =====

  // 발행 법인 목록을 노출하는 REST 가 하나도 없다(routes-draft·routes-body·routes-approval·
  // routes-render 전수 grep 0건). 프런트가 유효한 발행 법인 id 를 알 방법이 없어 값을 여기 둔다.
  // 이것은 두 번째 진실이므로 phase-04 검증블록의 **값 드리프트 게이트**가 서버 모듈
  // entity-registry.js 를 require 해 ISSUER_ENTITIES.length === 1 과 [0].id 동등을 단언한다 —
  // 법인이 둘이 되는 순간 게이트가 깨져 "어느 법인으로 발행하는가" 를 UI 로 물어야 한다는 사실이
  // 드러난다. 올바른 해법은 목록을 주는 REST 하나이며 그것은 lib/** 이라 이 트랙 밖이다.
  var BUSINESS_DOCUMENT_ISSUER_ENTITY_ID = "bigtion";

  // lib/chat/room-contracts.ts:7 ROOM_ID_RE 사본. 서버 collectRoomLocators(document-ops.ts:116)가
  // 이 정규식으로 방 locator 를 거르므로, 프런트가 다른 형태를 보내면 방 스코프 게이트
  // (resolveVisibleRoomForDocument)가 조용히 꺼진다. 값 동등은 인테이크 테스트가 서버 모듈의
  // ROOM_ID_RE.source 와 대조해 고정한다.
  var _ROOM_ID_RE = /^room:[a-z0-9][a-z0-9._-]*$/;

  // 서버 next.kind 값(lib/tools/business-documents/index.ts:62·:73). 명령 별칭 문자열이 아니라
  // 이 두 값이 판정자다 — 별칭이 늘어도 프런트는 따라갈 필요가 없다.
  var _INTAKE_KIND_DRAFT = "draft-intake";
  var _INTAKE_KIND_CHOOSE = "choose-document-type";

  var _INTAKE_TITLE_LABEL = "제목";
  var _INTAKE_COUNTERPARTY_LABEL = "상대방 이름";
  var _INTAKE_TITLE_PLACEHOLDER = "예: 2026년 8월 웹사이트 제작";
  var _INTAKE_COUNTERPARTY_PLACEHOLDER = "예: 주식회사 예시";
  var _INTAKE_SUBMIT_LABEL = "초안 만들기";
  var _MSG_INTAKE_PROMPT = "제목과 상대방 이름을 입력하면 초안을 만들어요.";

  // 제목·상대방명은 둘 다 z.string().min(1) 이다(contracts.ts:79·:57). 빈 값으로 보내면
  // invalid_body 400 만 돌아오므로 요청 자체를 보내지 않고 폼에서 막는다.
  var _MSG_INTAKE_TITLE_REQUIRED = "제목을 입력해 주세요.";
  var _MSG_INTAKE_COUNTERPARTY_REQUIRED = "상대방 이름을 입력해 주세요.";
  // sourceRefs 는 .min(1) 이라 비울 수 없다(contracts.ts:83). 방 id 도 세션 id 도 없으면
  // 실을 출처가 없다 — 서버가 400 으로 되돌려주기 전에 여기서 멈춘다.
  var _MSG_INTAKE_NO_SOURCE = "대화 출처를 확인할 수 없어 초안을 만들지 못했어요.";

  // ===== 미입력 항목 라벨 =====
  // lib/business-documents/body-completeness.ts REQUIRED_BODY_FIELDS 의 경로 집합 사본이다.
  // 배열 규칙은 `items[2].unitPrice` 형태로 나오므로(body-completeness.ts:94) 인덱스를 `[]` 로
  // 접은 키를 쓰고 `{n}` 에 1-기반 순번을 넣는다.
  // 매핑이 없는 경로는 **원문 그대로** 보여준다 — 매핑 누락을 빈 줄로 삼키지 않는다.
  // 두 표의 키 집합 동등은 actions 테스트가 서버 모듈에서 REQUIRED_BODY_FIELDS 를 읽어 고정한다.
  var _MISSING_LABELS: Record<string, string> = {
    "issueDate": "발행일",
    "validUntil": "견적 유효기간",
    "currency": "통화",
    "taxTreatment": "과세 구분",
    "items": "품목",
    "items[].lineNo": "{n}번째 품목의 순번",
    "items[].itemName": "{n}번째 품목의 품명",
    "items[].quantity": "{n}번째 품목의 수량",
    "items[].unitPrice": "{n}번째 품목의 단가",
    "vatRate": "부가세율",
    "totalAmount": "총액",
    "terms.deliveryPeriod": "납품 기한",
    "terms.paymentTerms": "지급 조건",
    "contractDate": "계약일",
    "client.representativeName": "상대방 대표자명",
    "client.businessRegistrationNumber": "상대방 사업자등록번호",
    "client.address": "상대방 주소",
    "client.phone": "상대방 연락처",
    "contractAmount": "계약 금액",
    "contractPeriodStart": "계약 시작일",
    "contractPeriodEnd": "계약 종료일",
    "deliverableSummary": "산출물 요약",
    "deliveryDate": "납품일",
    "delayPenaltyRatePerMille": "지체상금률",
    "paymentMilestones[].milestoneType": "{n}번째 지급 단계의 구분",
    "paymentMilestones[].ratio": "{n}번째 지급 단계의 비율",
    "paymentMilestones[].dueDate": "{n}번째 지급 단계의 지급일",
    "paymentMilestones[].paymentMethod": "{n}번째 지급 단계의 지급 방법",
    "clauses[].articleNo": "{n}번째 조항의 조 번호",
    "clauses[].articleTitle": "{n}번째 조항의 제목",
    "clauses[].body": "{n}번째 조항의 본문",
    "attachments": "부속 서류",
    "copiesCount": "작성 부수",
  };

  // ===== 유틸 =====

  function _isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function _str(value: unknown): string {
    return typeof value === "string" ? value : "";
  }

  // 초안 여부는 아티팩트 payload 의 스냅샷 사실이라 패널에 붙여둔다 — 스윕이 조회를 시작할 때
  // 모듈 전역 상태를 따로 들고 있지 않아도 패널 하나에서 documentId 와 함께 읽어낼 수 있다.
  function _isDraftPanel(panel: Element): boolean {
    return panel.getAttribute("data-bdc-draft") === "true";
  }

  function _apiDocument(documentId: string): string {
    return "/api/business-documents/" + encodeURIComponent(documentId);
  }

  // ws-handlers.ts 와 별도로 #chatMessages 를 직접 조회한다(work-inline-cards.ts 선례 미러) —
  // features/*.ts 는 views/chat.ts 보다 먼저 로드되므로 chat.ts 의 top-level chatMessages 를
  // 즉시 참조할 수 없다. 호출 시점(렌더 중)에는 DOM 이 항상 준비돼 있다.
  function _chatMessagesEl(): HTMLElement | null {
    return document.getElementById("chatMessages");
  }

  function _hasOwn(map: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(map, key);
  }

  // ===== 상태 -> 액션 판정 (순수 함수) =====

  /**
   * 이 문서 상태에서 사용자가 실행할 수 있는 액션 이름들. 표의 배열을 복사해 돌려준다 —
   * 호출자가 결과를 조작해도 판정 표가 오염되지 않는다.
   *
   * 6개 밖의 값(미상 상태)은 **빈 집합**이다. 폴백이 액션을 열면 서버가 새 상태를 하나 늘렸을
   * 때 그 상태가 조용히 전 액션을 얻는다.
   */
  function resolveDocumentCardActions(state: unknown): string[] {
    var key = typeof state === "string" ? state : "";
    return _hasOwn(_ACTIONS_BY_STATE, key) ? _ACTIONS_BY_STATE[key].slice() : [];
  }

  // ===== 세션·방·클라이언트 좌표 =====
  // 전부 typeof 가드다. 이 모듈은 classic script 전역이고, 컴파일 산출물을 new Function /
  // vm 샌드박스로 직접 실행하는 테스트 하네스는 window·wsClient 를 주입하지 않는다 —
  // 선언되지 않은 식별자를 그냥 참조하면 ReferenceError 다(chat.ts:579 선례).

  function _currentSessionId(): string | null {
    if (typeof window === "undefined") return null;
    return typeof window.currentSessionId === "string" ? window.currentSessionId : null;
  }

  // 발행/미리보기 산출물이 방으로 배달되려면 sessionId 와 roomId 가 **둘 다** 있어야 한다
  // (publish-ops.ts:202-207, artifact-service.ts:130). 짝이 없으면 서버가 배달을 건너뛴다 —
  // 라우터가 readText 로 빈 값을 undefined 로 접으므로 여기서는 있는 대로 실어 보내면 된다.
  function _currentRoomId(): string | null {
    if (typeof wsClient === "undefined" || !wsClient) return null;
    return typeof wsClient._currentRoomId === "string" ? wsClient._currentRoomId : null;
  }

  function _currentClientId(): string | null {
    if (typeof wsClient === "undefined" || !wsClient) return null;
    return typeof wsClient.clientId === "string" ? wsClient.clientId : null;
  }

  // ===== 미리보기·발행 후 시선 따라가기 (P6) =====
  // 이 클라이언트에서 render 액션을 실행했을 때만 새 카드로 스크롤한다 — 히스토리 로드나
  // 남이 만든 카드에 스크롤을 빼앗기지 않는다. 창은 액션 시작에 열리고, 새 패널이 스윕을
  // 통과하는 순간 소비되거나 시간으로 만료된다.
  var _FOLLOW_WINDOW_MS = 15000;
  var _followDocumentId = "";
  var _followUntil = 0;

  // 새 탭 열기(조용한 미리보기). 컴파일 산출물을 new Function 으로 실행하는 테스트 하네스는
  // window.open 을 주입하지 않는다 — typeof 가드가 필수다(다른 window 접근자들과 같은 관례).
  function _openExternal(href: string): void {
    if (typeof window === "undefined" || typeof window.open !== "function") return;
    window.open(href, "_blank", "noopener");
  }

  function _armFollow(documentId: string): void {
    _followDocumentId = documentId;
    _followUntil = Date.now() + _FOLLOW_WINDOW_MS;
  }

  function _disarmFollow(): void {
    _followDocumentId = "";
    _followUntil = 0;
  }

  // ===== 스냅샷 =====

  function _panelSnapshot(panel: Element): BusinessDocumentCardSnapshot {
    return {
      isDraft: _isDraftPanel(panel),
      documentNumber: _str(panel.getAttribute("data-bdc-number")),
      path: _str(panel.getAttribute("data-bdc-path")),
    };
  }

  /**
   * 이 카드가 가리키는 바이트가 **채번에 성공한 정식 발행본**임을 스냅샷만으로 증명하는가.
   *
   * 세 조건이 모두 참인 payload 는 발행 경로에서만 만들어진다 — publish-ops.ts:253-255 가
   * 채번에 성공한 뒤 :267-281 이 초안 분기 **밖**에서 이 payload 를 싣는다. 그래서 이 술어가
   * 참인데 레코드가 rendered 가 아니면 레코드 쪽이 뒤처졌거나 setState 가 실패한 것이다.
   */
  function _snapshotProvesPublication(panel: Element): boolean {
    var snapshot = _panelSnapshot(panel);
    return !snapshot.isDraft && snapshot.documentNumber !== "" && snapshot.path !== "";
  }

  function _wait(ms: number): Promise<void> {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /**
   * 다운로드는 상태가 아니라 **바이트**로 게이팅한다(§5).
   *
   * 초안 미리보기 산출물은 `${documentId}-draft-${contentHash}` 로 별도 파일이라 발행 후에도
   * 남는다(publish-ops.ts:211-216). 그래서 초안 카드가 재조회로 state: "rendered" 를 읽고
   * 다운로드를 내걸면 DRAFT 워터마크가 찍힌 PDF 를 정식 발행본인 양 준다.
   */
  function _canDownload(doc: Record<string, unknown>, snapshot: BusinessDocumentCardSnapshot): boolean {
    var documentNumber = _publishedDocumentNumber(doc);
    if (documentNumber === null) return false;
    if (snapshot.isDraft) return false;
    if (snapshot.path === "" || snapshot.documentNumber === "") return false;
    return snapshot.documentNumber === documentNumber;
  }

  /**
   * 발행 확정된 레코드의 **본문 문서번호**를 뽑는다. 발행본이 아니면 null.
   *
   * 발행본 판정의 레코드 축은 전부 여기 있다 — resolve 와 canDownload 가 이 하나를 공유하므로
   * 판정 로직 사본이 생기지 않는다. 문서번호는 봉투가 아니라 본문 키에 산다.
   */
  function _publishedDocumentNumber(doc: Record<string, unknown>): string | null {
    if (_str(doc.state) !== "rendered") return null;
    var numberKey = _NUMBER_KEY_BY_TYPE[_str(doc.documentType)];
    if (!numberKey) return null;
    var body = _isRecord(doc.body) ? doc.body : {};
    var value = body[numberKey];
    return typeof value === "string" && value !== "" ? value : null;
  }

  /**
   * 이 문서가 정식 발행본을 가지고 있는가, 그 산출물 URL 은 무엇인가 — **레코드만으로** 판정한다.
   * 카드가 아닌 표면(문서함 상세)이 소비하는 진입점이다.
   *
   * 발행본 파일명은 documentId 그 자체다(publish-ops.ts resolveOutputBaseName 의 비초안 분기).
   * 초안 미리보기는 `${documentId}-draft-${hash12}` 라는 **다른 파일**이라 이 URL 에는 초안
   * 바이트가 올 수 없다 — 그래서 아티팩트를 조회하지 않고도 URL 을 안다. 아티팩트 REST 를 쓰면
   * 문서 API 가 노출하지 않는 sessionId/roomId 까지 딸려와 "평면 문서 가시" 결정이 "평면
   * 룸-아티팩트 가시" 로 증폭된다.
   *
   * 대가는 파일명 규칙의 프런트 사본 1개다 — business-document-chat-card.test.ts 의 값 드리프트
   * 게이트가 컴파일된 publish-ops 를 실제로 호출해 그것을 묶는다.
   */
  function _resolvePublishedOutput(doc: Record<string, unknown>): BusinessDocumentPublishedOutput | null {
    if (_publishedDocumentNumber(doc) === null) return null;
    var documentId = _str(doc.id);
    // 소비자가 이 값을 href 에 그대로 넣는다 — 슬러그 규칙 밖 id 는 URL 을 만들지 않는다.
    if (!_DOCUMENT_ID_RE.test(documentId)) return null;
    var base = _OUTPUT_URL_PREFIX + "/" + documentId;
    return { pdfHref: base + ".pdf", htmlHref: base + ".html" };
  }

  // ===== 미입력 항목 라벨 =====

  function _missingLabel(dotPath: string): string {
    var normalized = dotPath.replace(/\[\d+\]/g, "[]");
    // 매핑 누락을 빈 줄로 삼키지 않는다 — 원문 dot-path 를 그대로 보여준다.
    if (!_hasOwn(_MISSING_LABELS, normalized)) return dotPath;
    var index = dotPath.match(/\[(\d+)\]/);
    var ordinal = index ? String(Number(index[1]) + 1) : "";
    return _MISSING_LABELS[normalized].replace("{n}", ordinal);
  }

  // ===== HTTP =====

  function _apiAction(documentId: string, segment: string): string {
    return _apiDocument(documentId) + "/" + segment;
  }

  function _errorCopy(code: string): string {
    // 코드 없음 = 서버에 닿지 못했거나 응답을 읽지 못했다(클라이언트 쪽 실패).
    if (code === "") return _MSG_ACTION_NETWORK;
    // 표에 없는 코드는 **코드 그대로** 노출한다 — 침묵 금지.
    return _hasOwn(_ERROR_COPY, code) ? _ERROR_COPY[code] : code;
  }

  function _readErrorCode(payload: Record<string, unknown>): string {
    // 비정상 응답인데 코드가 없으면 서버 쪽 실패다(http-errors.ts:104 의 폴백과 같은 값).
    return typeof payload.error === "string" && payload.error !== "" ? payload.error : "internal_error";
  }

  interface BusinessDocumentCardResult {
    ok: boolean;
    code: string;
    payload: Record<string, unknown>;
  }

  async function _request(url: string, init: Record<string, unknown>): Promise<BusinessDocumentCardResult> {
    try {
      var response = await fetch(url, init);
      var raw: unknown = null;
      try {
        raw = await response.json();
      } catch {
        raw = null;
      }
      var payload = _isRecord(raw) ? raw : {};
      if (!response.ok) return { ok: false, code: _readErrorCode(payload), payload: payload };
      return { ok: true, code: "", payload: payload };
    } catch {
      return { ok: false, code: "", payload: {} };
    }
  }

  function _get(url: string): Promise<BusinessDocumentCardResult> {
    return _request(url, { credentials: "same-origin" });
  }

  function _post(url: string, body: Record<string, unknown>): Promise<BusinessDocumentCardResult> {
    return _request(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  // ===== 문서당 카드 1개 불변식 =====
  // 같은 문서의 카드가 한 방에 여럿 있을 수 있다 — D-QUIET-PREVIEW(2026-08-30) 이후 초안
  // 미리보기는 카드를 만들지 않지만, 발행 카드 + 독립 카드 + 그 이전에 쌓인 미리보기 카드들이
  // 히스토리에 실재한다(아티팩트는 append-only 라 소급 삭제되지 않는다). 카드들은 서로 다른
  // contentHash 의 실재하는 PDF 스냅샷이라 지우지 않는다.
  // 대신 **DOM 상 마지막 카드에만** 상태 패널을 남긴다 — 같은 문서를 향해 서로 다른 상태를
  // 주장하는 패널이 여럿 생기지 않도록. 앞선 카드는 제목 + PDF 링크만 남는 히스토리 항목이 된다.
  //
  // 판정은 **decorate 호출 순서가 아니라 DOM 순서**로 한다. 호출 순서로 판정하면 load-more 가
  // 깨진다: onMoreMessages(ws-render-helpers.ts:236-240)는 renderHistoryMessage 로 노드를 만든 뒤
  // wsChatMessagesElement.insertBefore(node, anchor) 로 **앞에** 꽂는다. 즉 나중에 decorate 되는
  // 카드가 더 **오래된** 카드다 — 호출 순서 판정은 가장 오래된 카드가 최신 카드의 패널을
  // 빼앗는 결과를 낸다.
  //
  // decorate 시점에는 새 카드가 아직 삽입되지 않았으므로(renderArtifactCard 가 만든 뒤 호출부가
  // append/insertBefore 한다) 스윕을 마이크로태스크로 미룬다. 한 배치의 삽입이 모두 끝난 뒤
  // 한 번만 돌면서 DOM 순서로 판정한다.
  var _pendingPanels: HTMLElement[] = [];
  var _sweepScheduled = false;

  function _scheduleSweep(): void {
    if (_sweepScheduled) return;
    _sweepScheduled = true;
    void Promise.resolve().then(function () {
      _sweepScheduled = false;
      _sweep();
    });
  }

  function _sweep(): void {
    _pruneDuplicatePanels();

    // 살아남은 패널만 조회한다 — 곧 강등될 패널에 요청을 보내면 한 배치가 같은 문서를
    // 중복 조회한다. 강등된 패널은 parentNode 가 끊겨 있으므로 그것으로 판별한다.
    var pending = _pendingPanels;
    _pendingPanels = [];
    pending.forEach(function (panel) {
      if (!panel.parentNode) return;
      void _load(panel, _str(panel.getAttribute("data-business-document-id")));
    });

    _followNewPanel(pending);
  }

  /**
   * 미리보기·발행 직후 도착한 새 카드로 스크롤을 옮긴다(P6). 판정은 "이번 스윕 배치에 그 문서의
   * 생존 패널이 실제로 들어왔는가" 다 — 액션과 무관한 스윕(히스토리 로드)은 창이 열려 있어도
   * 그 문서의 패널이 배치에 없으면 아무것도 하지 않는다.
   */
  function _followNewPanel(pending: HTMLElement[]): void {
    if (_followDocumentId === "") return;
    if (Date.now() > _followUntil) {
      _disarmFollow();
      return;
    }
    var host = _chatMessagesEl();
    if (!host) return;
    for (var index = 0; index < pending.length; index += 1) {
      var panel = pending[index];
      if (!panel.parentNode) continue;
      if (_str(panel.getAttribute("data-business-document-id")) !== _followDocumentId) continue;
      host.scrollTop = host.scrollHeight;
      _disarmFollow();
      return;
    }
  }

  // 강등된 카드에 남기는 이동 안내(P6). 버튼이 "사라진" 게 아니라 최신 카드로 "이동했다"는
  // 사실을 그 자리에 말한다 — 이 문구가 없으면 미리보기 직후 사용자는 고장으로 읽는다.
  var _MSG_MOVED_NOTE = "최신 카드로 이동했어요 ↓";

  function _pruneDuplicatePanels(): void {
    var host = _chatMessagesEl();
    if (!host) return;
    // querySelectorAll 은 문서 순서로 돌려준다 — 같은 documentId 는 마지막 것만 남긴다.
    var panels = host.querySelectorAll(".bdc-panel");
    var survivor = new Map<string, Element>();
    for (var index = 0; index < panels.length; index += 1) {
      var documentId = _str(panels[index].getAttribute("data-business-document-id"));
      if (documentId === "") continue;
      survivor.set(documentId, panels[index]);
    }
    for (var cursor = 0; cursor < panels.length; cursor += 1) {
      var panel = panels[cursor];
      var key = _str(panel.getAttribute("data-business-document-id"));
      if (key === "" || survivor.get(key) === panel) continue;
      var parent = panel.parentNode as HTMLElement | null;
      if (!parent) continue;
      parent.removeChild(panel);
      // 아티팩트 카드 안에서 강등된 경우에만 흔적을 남긴다. 독립 카드(부모 = chatMessages)는
      // 패널이 곧 카드 전체라 안내를 남길 자리가 없고, 타임라인 루트에 문구를 흘리면 안 된다.
      if (parent === host) continue;
      if (parent.querySelector(".bdc-moved-note")) continue;
      var note = document.createElement("div");
      note.className = "bdc-moved-note";
      note.textContent = _MSG_MOVED_NOTE;
      parent.appendChild(note);
    }
  }

  // ===== 투영 =====

  // 완성도 카운터·액센트가 적용되는 상태 — 본문이 아직 열려 있는(제출 전/반려 후) 두 상태다.
  function _isAuthoringState(stateKey: string): boolean {
    return stateKey === "draft" || stateKey === "needs_input";
  }

  function _projectDocumentView(doc: Record<string, unknown>, snapshot: BusinessDocumentCardSnapshot, missingCount: number | null): BusinessDocumentCardView {
    var rawType = _str(doc.documentType);
    var rawState = _str(doc.state);
    // 도달성: state 는 z.enum 6종이고 서버가 전건 parse 한다(contracts.ts:48-56).
    // 미지 상태는 변형 없이 원문만 보여주고 크래시하지 않는다.
    var stateKey = Object.prototype.hasOwnProperty.call(_STATE_LABELS, rawState) ? rawState : "";
    var body = _isRecord(doc.body) ? doc.body : {};

    var rawNumber = body[_NUMBER_KEY_BY_TYPE[rawType]];
    var hasNumber = typeof rawNumber === "string" && rawNumber !== "";
    var unassigned = _UNASSIGNED_NUMBER[stateKey] || _UNASSIGNED_FALLBACK;

    // 도달성: counterparty 는 스키마상 필수이고 displayName 은 min(1)(contracts.ts:58/:81) —
    // 대체표기는 스키마 밖 입력에 대한 방어다.
    var counterparty = _isRecord(doc.counterparty) && typeof doc.counterparty.displayName === "string"
      ? doc.counterparty.displayName
      : _EMPTY_VALUE;

    // 액션 집합의 유일한 판정자는 resolveDocumentCardActions 다. 여기서 상태를 다시 보지 않는다.
    var actions = resolveDocumentCardActions(stateKey);
    var notice = "";
    var downloadHref = "";
    if (actions.indexOf("download") >= 0) {
      if (_canDownload(doc, snapshot)) {
        downloadHref = snapshot.path;
      } else {
        // 상태는 rendered 인데 이 카드가 가리키는 바이트가 정식 발행본이 아니다.
        // 버튼을 지우고 왜 없는지 말한다 — 조용히 사라지면 사용자는 기능이 고장났다고 읽는다.
        actions = actions.filter(function (action) { return action !== "download"; });
        notice = _MSG_DRAFT_SNAPSHOT;
      }
    }

    // 액센트는 완성도를 따른다(P1·P3, 사용자 승인 2026-08-29). 작성 단계(draft·needs_input)에서
    // 완성도를 알면 — 미완성이면 [수정], 완성이면 [승인 요청]이 다음 걸음이다. needs_input 에만
    // 있던 규칙(card.ts 구 :159)을 두 상태로 통일한 것이다. 완성도를 모르면(조회 실패) 표의
    // 기본값으로 물러난다 — 빈 초안의 액센트가 승인 요청으로 되돌아가는 것이 대가이지만,
    // 추측으로 강조를 고르는 것보다 낫다. 승인 요청 자체는 막지 않는다 — 제출 차단은 기각된
    // (b)안이다(웨이브4 인계 §3-1, 검토자 책임 유지).
    var primaryAction = _hasOwn(_PRIMARY_ACTION_BY_STATE, stateKey) ? _PRIMARY_ACTION_BY_STATE[stateKey] : "";
    if (_isAuthoringState(stateKey) && missingCount !== null) {
      primaryAction = missingCount > 0 ? "edit" : "submit";
    }

    return {
      stateKey: stateKey,
      stateLabel: _STATE_LABELS[stateKey] || rawState,
      rows: [
        { label: _LABEL_NUMBER, value: hasNumber ? String(rawNumber) : unassigned.text, unassigned: hasNumber ? "" : unassigned.kind },
        { label: _LABEL_COUNTERPARTY, value: counterparty, unassigned: "" },
      ],
      message: "",
      isDraft: snapshot.isDraft,
      actions: actions,
      primaryAction: primaryAction,
      notice: notice,
      downloadHref: downloadHref,
      // 카운터는 작성 단계에서만 그린다 — 제출 이후 상태의 완성도는 검토·발행 축의 일이다.
      missingCount: _isAuthoringState(stateKey) ? missingCount : null,
      rejectionReason: _isAuthoringState(stateKey) && _isRecord(doc.rejection) && typeof doc.rejection.reason === "string"
        ? doc.rejection.reason
        : "",
    };
  }

  function _errorMessage(status: number | null): string {
    if (status === 404) return _MSG_NOT_FOUND;
    if (status === 401 || status === 403) return _MSG_UNAUTHORIZED;
    if (status !== null && status >= 400 && status < 500) return _MSG_BAD_REQUEST;
    return _MSG_FETCH_FAILED;
  }

  // ===== 렌더 =====
  // 문서 제목·상대방명·문서번호는 외부 입력이다 — 전부 textContent 로 넣고 데이터로 마크업을
  // 조립하지 않는다(inbox.ts _appendSpan 선례).

  function _appendSpan(parent: HTMLElement, className: string, text: string): HTMLElement {
    var span = document.createElement("span");
    span.className = className;
    span.textContent = text;
    parent.appendChild(span);
    return span;
  }

  /**
   * 여정 스테퍼(P2). 4걸음 중 현재 상태의 자리를 채워 "버튼이 여정의 어느 걸음인가" 를 배치로
   * 읽게 한다. 현재 걸음의 라벨은 _STATE_LABELS 그대로다 — needs_input("입력 필요") 처럼 여정
   * 라벨("초안")과 상태 라벨이 다른 상태에서 어휘가 사라지지 않게 하고, inbox 와의 라벨 값
   * 동등(어휘 게이트)도 이 한 표로 유지된다.
   */
  function _buildStepper(stateKey: string, stateLabel: string): HTMLElement {
    var stepper = document.createElement("div");
    stepper.className = "bdc-stepper";
    stepper.setAttribute("data-state", stateKey);
    var nowIndex = _JOURNEY_INDEX[stateKey];
    _JOURNEY_STEPS.forEach(function (step, index) {
      if (index > 0) {
        var conn = document.createElement("span");
        conn.className = "bdc-step-conn";
        if (index <= nowIndex) conn.setAttribute("data-done", "true");
        stepper.appendChild(conn);
      }
      var node = document.createElement("span");
      node.className = "bdc-step";
      node.setAttribute("data-step", step.key);
      node.setAttribute("data-step-state", index < nowIndex ? "done" : index === nowIndex ? "now" : "todo");
      _appendSpan(node, "bdc-step-node", "");
      _appendSpan(node, "bdc-step-label", index === nowIndex ? stateLabel : step.label);
      stepper.appendChild(node);
    });
    return stepper;
  }

  function _renderPanel(panel: HTMLElement, view: BusinessDocumentCardView): void {
    // 더 최신 카드에 밀려 떨어져 나간 패널에는 그리지 않는다 — 늦게 도착한 응답이
    // 이미 은퇴한 패널을 되살리면 문서당 카드 1개 불변식이 무너진다.
    if (!panel.parentNode) return;
    panel.innerHTML = "";

    var head = document.createElement("div");
    head.className = "bdc-panel-head";
    if (_hasOwn(_JOURNEY_INDEX, view.stateKey)) {
      // 정상 경로 4상태 + needs_input 은 스테퍼로 그린다(P2).
      head.appendChild(_buildStepper(view.stateKey, view.stateLabel));
    } else {
      // void·미지 상태·로딩·실패는 pill 폴백 — 여정 밖 사실에 걸음을 발명하지 않는다.
      var pill = _appendSpan(head, "ax-status-pill", view.stateLabel);
      // 미지 상태에는 data-state 를 붙이지 않는다 — 없는 변형을 주장하느니 기본 pill 이 낫다.
      if (view.stateKey !== "") pill.setAttribute("data-state", view.stateKey);
    }
    // 초안 배지는 아티팩트 payload 의 스냅샷 사실이다(문서의 현재 상태와 별개) —
    // 이 카드가 가리키는 PDF 바이트가 초안본이라는 뜻이므로 조회 성공·실패와 무관하게 붙는다.
    if (view.isDraft) _appendSpan(head, "bdc-draft-badge", _DRAFT_BADGE);
    panel.appendChild(head);

    // 완성도 카운터(P3). 왜 지금 액센트가 [수정]인지(또는 [승인 요청]인지)를 이 한 줄이
    // 설명한다. 완성도를 모르면(null) 아무것도 주장하지 않는다.
    if (view.missingCount !== null) {
      var progress = document.createElement("div");
      progress.className = "bdc-progress";
      if (view.missingCount > 0) {
        progress.setAttribute("data-bdc-complete", "false");
        progress.textContent = _MSG_MISSING_COUNT_PREFIX + String(view.missingCount) + _MSG_MISSING_COUNT_SUFFIX;
      } else {
        progress.setAttribute("data-bdc-complete", "true");
        progress.textContent = _MSG_BODY_COMPLETE;
      }
      panel.appendChild(progress);
    }

    // 반려 사유. 사유 원문은 외부 입력이므로 textContent 로만 넣는다.
    if (view.rejectionReason !== "") {
      var rejected = document.createElement("div");
      rejected.className = "bdc-rejected-note";
      _appendSpan(rejected, "bdc-rejected-title", _MSG_REJECTED_TITLE);
      var reasonEl = document.createElement("div");
      reasonEl.className = "bdc-rejected-reason";
      reasonEl.textContent = view.rejectionReason;
      rejected.appendChild(reasonEl);
      panel.appendChild(rejected);
    }

    if (view.rows.length > 0) {
      var meta = document.createElement("div");
      meta.className = "bdc-meta";
      view.rows.forEach(function (row) {
        var rowEl = document.createElement("div");
        rowEl.className = "bdc-meta-row";
        _appendSpan(rowEl, "bdc-meta-label", row.label);
        var value = _appendSpan(rowEl, "bdc-meta-value", row.value);
        if (row.unassigned !== "") value.setAttribute("data-unassigned", row.unassigned);
        meta.appendChild(rowEl);
      });
      panel.appendChild(meta);
    }

    if (view.message !== "") {
      var message = document.createElement("div");
      message.className = "bdc-message";
      message.textContent = view.message;
      panel.appendChild(message);
    }

    if (view.isDraft) {
      var warning = document.createElement("div");
      warning.className = "bdc-draft-warning";
      warning.textContent = _MSG_DRAFT_WARNING;
      panel.appendChild(warning);
    }

    if (view.notice !== "") {
      var notice = document.createElement("div");
      notice.className = "bdc-notice";
      notice.textContent = view.notice;
      panel.appendChild(notice);
    }

    _renderActions(panel, view);
  }

  // 마지막으로 그린 문서 레코드. 본문 폼이 초기값으로 쓴다 — 폼이 스스로 GET /:id 를 한 번 더
  // 하면 방금 그린 것과 같은 봉투를 두 번 받는다. WeakMap 이라 패널이 DOM 에서 떨어지면 함께
  // 회수된다(문서당 카드 1개 불변식이 패널을 수시로 강등한다).
  var _documentByPanel = new WeakMap<HTMLElement, Record<string, unknown>>();

  // 마지막으로 알게 된 완성도(missing 경로 수). 조회(_load)와 본문 폼 저장 응답이 갱신한다.
  // 오류 재렌더처럼 완성도를 새로 알 수 없는 경로에서는 이 값을 유지한다 — 액센트가 표의
  // 기본값으로 되튀면 사용자는 다음 걸음이 바뀌었다고 읽는다.
  var _missingCountByPanel = new WeakMap<HTMLElement, number>();

  /**
   * 조회·액션 응답이 준 레코드로 패널을 그리는 유일한 지점. 폼의 초기값도 여기서 갱신된다.
   *
   * @param missingCount 숫자 = 새로 알게 된 완성도 / null = 미상으로 재설정 / undefined = 유지.
   */
  function _renderDocument(panel: HTMLElement, doc: Record<string, unknown>, missingCount?: number | null): void {
    _documentByPanel.set(panel, doc);
    if (missingCount === null) _missingCountByPanel.delete(panel);
    else if (typeof missingCount === "number") _missingCountByPanel.set(panel, missingCount);
    var known = _missingCountByPanel.has(panel) ? (_missingCountByPanel.get(panel) as number) : null;
    _renderPanel(panel, _projectDocumentView(doc, _panelSnapshot(panel), known));
  }

  function _renderStatusOnly(panel: HTMLElement, stateLabel: string, message: string): void {
    // stateKey 가 "" 이므로 resolveDocumentCardActions 가 빈 집합을 준다 — 로딩·실패 패널에는
    // 액션이 붙지 않는다. 상태를 모르는 채로 액션을 여는 경로를 만들지 않기 위해서다.
    _renderPanel(panel, {
      stateKey: "",
      stateLabel: stateLabel,
      rows: [],
      message: message,
      isDraft: _isDraftPanel(panel),
      actions: [],
      primaryAction: "",
      notice: "",
      downloadHref: "",
      missingCount: null,
      rejectionReason: "",
    });
  }

  // ===== 액션 스트립 =====
  // 리스너는 **요소에 직접** 건다(work-inline-cards.ts:307·:310 선례). chatMessages 에 세 번째
  // 위임을 추가하지 않는다 — chat.ts:595(.artifact-card -> ArtifactPanel.open)와 chat.ts:749
  // (답장·인용) 두 위임은 같은 노드의 형제라 서로를 stopPropagation 으로 막지 못한다.
  // 버튼에서 stopPropagation 하면 버블링이 chatMessages 에 닿지 않으므로 두 위임 모두 조용해진다.

  function _wireAction(el: HTMLElement, run: (() => void) | null): void {
    el.addEventListener("click", function (event: Event) {
      // 첫 줄에서 막는다 — 액션 클릭이 ArtifactPanel 을 함께 열면 카드가 두 가지 일을 한다.
      event.stopPropagation();
      if (run) run();
    });
  }

  /**
   * 표시 순서(P1): 보조·후퇴 액션을 파이프라인 순서로 왼쪽에, 전진 액센트를 맨 끝(오른쪽)에.
   * CSS 가 .bdc-action-primary 에 margin-left:auto 를 줘 양끝 정렬이 된다 — 어느 상태에서든
   * "오른쪽 끝을 누르면 다음 단계" 가 성립한다. 집합 자체는 위에서 이미 정해졌고 여기서는
   * 순서만 바꾼다.
   */
  function _orderActionsForDisplay(actions: string[], primaryAction: string): string[] {
    var ordered = actions.slice().sort(function (a, b) {
      return (_ACTION_DISPLAY_RANK[a] || 99) - (_ACTION_DISPLAY_RANK[b] || 99);
    });
    if (primaryAction !== "" && ordered.indexOf(primaryAction) >= 0) {
      ordered = ordered.filter(function (action) { return action !== primaryAction; });
      ordered.push(primaryAction);
    }
    return ordered;
  }

  function _renderActions(panel: HTMLElement, view: BusinessDocumentCardView): void {
    if (view.actions.length === 0) return;

    var strip = document.createElement("div");
    strip.className = "bdc-actions";

    _orderActionsForDisplay(view.actions, view.primaryAction).forEach(function (action) {
      var isPrimary = action === view.primaryAction;
      // 후퇴·파기(반려·폐기)는 조용한 위험 톤 — 전진 액센트와 시각적으로 분리한다(P1).
      var isDanger = !isPrimary && _DANGER_ACTIONS.indexOf(action) >= 0;
      if (action === "download") {
        // 서버 호출 0 — 발행 시점 스냅샷 경로를 그대로 연다. 리스너는 위임 차단만 한다.
        var link = document.createElement("a");
        link.className = "ui-btn bdc-action-btn" + (isPrimary ? " bdc-action-primary" : "");
        link.setAttribute("href", view.downloadHref);
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener");
        link.setAttribute("data-bdc-action", action);
        link.textContent = _ACTION_LABELS[action];
        _wireAction(link, null);
        strip.appendChild(link);
        return;
      }

      var button = document.createElement("button");
      button.type = "button";
      button.className = "ui-btn bdc-action-btn bdc-action"
        + (isPrimary ? " bdc-action-primary" : "")
        + (isDanger ? " bdc-action-danger" : "");
      button.setAttribute("data-bdc-action", action);
      // 미완성이 확인된 문서의 edit 는 "내용 입력" 이다 — 고칠 것이 있어야 "수정" 이다.
      button.textContent = action === "edit" && view.missingCount !== null && view.missingCount > 0
        ? _ACTION_LABEL_EDIT_EMPTY
        : _ACTION_LABELS[action];
      _wireAction(button, function () { _startAction(panel, action); });
      strip.appendChild(button);
    });

    panel.appendChild(strip);
  }

  // 서버를 부르는 버튼만 잠근다. download 는 .bdc-action 이 아니라 잠금 대상이 아니다 —
  // rendered 상태의 액션 집합은 [download] 하나뿐이라 동시 진행할 서버 액션이 없다.
  function _setActionsBusy(panel: HTMLElement, busy: boolean): void {
    var buttons = panel.querySelectorAll<HTMLButtonElement>(".bdc-action");
    for (var index = 0; index < buttons.length; index += 1) {
      buttons[index].disabled = busy;
    }
  }

  // 인라인 오류는 카드 **안**에 남긴다. 토스트만 쓰면 방에 카드가 여럿일 때 어느 카드의 실패인지
  // 사라진다. 같은 패널의 이전 오류는 지운다 — 실패가 쌓여 목록이 되지 않게.
  function _showActionError(panel: HTMLElement, text: string, items: string[]): void {
    if (!panel.parentNode) return;
    var previous = panel.querySelector(".bdc-action-error");
    if (previous && previous.parentNode) previous.parentNode.removeChild(previous);

    var box = document.createElement("div");
    box.className = "bdc-action-error";
    var message = document.createElement("div");
    message.className = "bdc-action-error-text";
    message.textContent = text;
    box.appendChild(message);

    // 빈 목록을 그리지 않는다 — 항목이 0개면 목록 자체가 없다(§4).
    if (items.length > 0) {
      var list = document.createElement("ul");
      list.className = "bdc-missing-list";
      items.forEach(function (item) {
        var row = document.createElement("li");
        row.textContent = item;
        list.appendChild(row);
      });
      box.appendChild(list);
    }

    panel.appendChild(box);
  }

  // ===== 조회 =====
  // 아티팩트 payload 는 발행 시점 스냅샷이다 — buildBusinessDocumentArtifactRecord
  // (publish-ops.ts:171-205)가 싣는 것은 { path, htmlPath, mime, documentId, documentNumber,
  // contentHash, isDraft } 뿐이고 state 는 없다. 초안 카드가 남은 방에서 문서가 승인·발행되면
  // payload.isDraft 는 계속 true 다. 그래서 상태·번호·상대방은 반드시 재조회 결과로 그린다.
  //
  // credentials: "same-origin" 을 명시한다 — 문서 라우트는 쿠키 게이트다(inbox.ts:517 관용).
  //
  // 화해 재조회 대기(ms). 발행 아티팩트가 방으로 배달되는 시점은 서버가
  // store.setState(record.id, "rendered") 를 쓰기 **전**이다(publish-ops.ts:274 -> :298). 그 창에서
  // decorate 의 조회가 앞지르면 approved 를 읽는다. 이 값은 모듈이 소유하는 단 하나의 숫자이고
  // 테스트는 reconcileDelayMs 로 그것을 읽어 대기한다 — 테스트에 숫자를 다시 적으면 두 번째
  // 진실이 되어 값을 바꾼 날 게이트가 조용히 어긋난다.
  var _RECONCILE_DELAY_MS = 150;

  /**
   * 작성 단계(draft·needs_input)는 완성도를 함께 조회해 그린다(P3) — 카운터와 액센트의 근거다.
   * 그 밖의 상태는 곧바로 그린다. 완성도 조회 실패는 미상(null)으로 접어 표의 기본 강조로
   * 물러난다(_get 이 모든 실패를 접는다).
   */
  async function _renderWithCompleteness(panel: HTMLElement, documentId: string, doc: Record<string, unknown>): Promise<void> {
    if (!_isAuthoringState(_str(doc.state))) {
      _renderDocument(panel, doc);
      return;
    }
    var assessed = await _get(_apiAction(documentId, "completeness"));
    // 대기 중 강등됐으면 그리지 않는다 — _renderPanel 부모 가드와 같은 규칙.
    if (!panel.parentNode) return;
    var completeness = assessed.ok && _isRecord(assessed.payload.completeness) ? assessed.payload.completeness : null;
    var rawMissing = completeness && Array.isArray(completeness.missing) ? completeness.missing : null;
    var missingCount = rawMissing === null ? null : rawMissing.filter(function (item: unknown) {
      return typeof item === "string" && item !== "";
    }).length;
    _renderDocument(panel, doc, missingCount);
  }

  /**
   * @param allowReconcile 화해 재조회를 **한 번만** 허용한다. 재귀 호출이 false 를 넘겨 두 번째
   *   조회에서는 무조건 그린다 — 무한 폴링이 되지 않는 이유가 이 한 인자다.
   */
  async function _load(panel: HTMLElement, documentId: string, allowReconcile = true): Promise<void> {
    try {
      var response = await fetch(_apiDocument(documentId), { credentials: "same-origin" });
      if (!response.ok) {
        _renderStatusOnly(panel, _PILL_ERROR, _errorMessage(response.status));
        return;
      }
      var payload: unknown = await response.json();
      if (!_isRecord(payload) || !_isRecord(payload.document)) {
        throw new Error("Invalid business document detail payload");
      }
      var doc = payload.document;

      // 스냅샷이 발행을 증명하는데 레코드가 rendered 가 아니면 두 경우뿐이다.
      //   (i)  setState 가 아직 안 끝났다 -> 한 번 더 물으면 rendered 를 본다.
      //   (ii) setState 가 실패했다 -> 다시 물어도 approved 다. 그때는 "승인됨 / [발행]" 이
      //        **진실**이고 재발행이 정상 복구 경로다(publish-ops.ts:20-22 가역성).
      // 두 경우 모두 두 번째 조회에서 진실로 수렴하므로 화해는 정확히 1회면 충분하다.
      if (allowReconcile && _str(doc.state) !== "rendered" && _snapshotProvesPublication(panel)) {
        await _wait(_RECONCILE_DELAY_MS);
        // 대기 중 더 최신 카드에 밀려 강등됐으면 포기한다 — _renderPanel 부모 가드와 같은 규칙이고,
        // 여기서 포기하지 않으면 은퇴한 패널에 조회를 한 번 더 건다.
        if (!panel.parentNode) return;
        await _load(panel, documentId, false);
        return;
      }

      await _renderWithCompleteness(panel, documentId, doc);
    } catch {
      // 네트워크 실패 · JSON 파싱 실패 · 응답 형태 불일치
      _renderStatusOnly(panel, _PILL_ERROR, _errorMessage(null));
    }
  }

  // ===== 액션 실행 =====
  // 낙관적 갱신을 하지 않는다. 프런트가 다음 상태를 추측하면 서버의 전이 규칙이 두 곳에 생긴다 —
  // 성공 후에는 반드시 서버가 준 레코드로 다시 그린다(work-inline-cards.ts:364-395 선례 미러).

  /**
   * POST /:id/render 는 문서 레코드를 돌려주지 않는다(routes-render.ts:11-12 — 레코드를 반사하면
   * 응답 표면이 문서 본문 전체로 넓어진다). 그래서 preview·publish 만 재조회로 다시 그리고,
   * submit·approve·reject 는 응답의 document 로 그린다. 이 비대칭을 한 규칙으로 뭉뚱그리면
   * 발행 직후 카드가 상태 소스를 잃는다.
   */
  function _isRenderAction(action: string): boolean {
    return action === "preview" || action === "publish";
  }

  // preview 와 publish 는 **같은 엔드포인트**다. 갈라지는 것은 서버 쪽 isDraft 판정
  // (publish-ops.ts:251 isBusinessDocumentDraft(record.state))뿐이고 클라이언트는 상태를 보내지
  // 않는다 — 클라이언트가 초안 여부를 주장할 수 있게 두면 그 주장이 채번 분기의 두 번째 진실이 된다.
  function _renderRequestBody(): Record<string, unknown> {
    return {
      sessionId: _currentSessionId(),
      roomId: _currentRoomId(),
      clientId: _currentClientId(),
    };
  }

  function _sendAction(action: string, documentId: string, reason: string): Promise<BusinessDocumentCardResult> {
    if (_isRenderAction(action)) return _post(_apiAction(documentId, "render"), _renderRequestBody());
    if (action === "submit") return _post(_apiAction(documentId, "submit"), {});
    if (action === "approve") return _post(_apiAction(documentId, "approve"), {});
    if (action === "void") return _post(_apiAction(documentId, "void"), {});
    // 요청 본문에 반려 사유만 싣는다. 서버가 문서 레코드에서 재구성하는 것이 유일한 합법
    // 경로다(routes-approval.ts:104-107).
    return _post(_apiAction(documentId, "reject"), { reason: reason });
  }

  /**
   * 사용자 확인이 필요한 액션은 **버튼을 잠그기 전에** 확인을 끝낸다. 잠그고 나서 모달을 띄우면
   * 사용자가 취소했을 때 모달 유틸에 취소 콜백이 없어(confirm-modal.ts:87-105) 잠금을 풀 기회가
   * 없고 카드가 영구히 잠긴다.
   */
  function _startAction(panel: HTMLElement, action: string): void {
    if (action === "edit") {
      _openBodyForm(panel);
      return;
    }

    if (action === "reject") {
      _openRejectForm(panel);
      return;
    }

    if (action === "publish") {
      // approved -> rendered 는 채번을 확정하고 역방향 전이가 없다(contracts.ts:384-392).
      // 무엇을 발행하는지 요약을 함께 보여준다 — 카드가 이미 든 레코드에서 그리므로 서버 호출 0.
      var summary = _publishSummary(_documentByPanel.get(panel) || null);
      _confirmIrreversible(_CONFIRM_PUBLISH_TITLE,
        summary === "" ? _CONFIRM_PUBLISH_BODY : summary + " — " + _CONFIRM_PUBLISH_BODY,
        _CONFIRM_PUBLISH_WARNING,
        _CONFIRM_PUBLISH_LABEL, function () { void _runAction(panel, action, ""); });
      return;
    }

    if (action === "void") {
      // void 는 나가는 전이가 없는 종착 상태다(contracts.ts BUSINESS_DOCUMENT_TRANSITIONS) —
      // publish 와 같은 비가역 확인을 거친다.
      _confirmIrreversible(_CONFIRM_VOID_TITLE, _CONFIRM_VOID_BODY, _CONFIRM_VOID_WARNING,
        _CONFIRM_VOID_LABEL, function () { void _runAction(panel, action, ""); });
      return;
    }

    void _runAction(panel, action, "");
  }

  /**
   * 반려 사유 미니폼(P5). window.prompt 대신 카드 안에서 펼쳐진다 — 본문 폼과 같은 관용구다.
   * 열려 있는 동안 액션 스트립을 숨겨 승인·미리보기가 함께 눌리지 않게 한다(모달 대신 자리
   * 교체). "반려 확정" 전까지는 아무 요청도 나가지 않는다.
   */
  function _openRejectForm(panel: HTMLElement): void {
    // 멱등 — 이미 열려 있으면 그대로 둔다(더블클릭이 폼을 두 벌 만들지 않게).
    if (panel.querySelector(".bdc-reject-form")) return;

    var strip = panel.querySelector(".bdc-actions") as HTMLElement | null;
    if (strip) strip.hidden = true;

    var form = document.createElement("div");
    form.className = "bdc-reject-form";
    _appendSpan(form, "bdc-reject-title", _REJECT_FORM_TITLE);

    var reason = document.createElement("textarea") as HTMLTextAreaElement;
    reason.className = "bdc-field-input bdc-reject-reason";
    form.appendChild(reason);

    var error = document.createElement("div");
    error.className = "bdc-form-error";
    error.setAttribute("data-bdc-visible", "false");
    form.appendChild(error);

    var actions = document.createElement("div");
    actions.className = "bdc-reject-actions";

    var confirm = document.createElement("button");
    confirm.type = "button";
    confirm.className = "ui-btn bdc-form-btn bdc-reject-confirm";
    confirm.setAttribute("data-bdc-reject", "confirm");
    confirm.textContent = _REJECT_CONFIRM_LABEL;
    _wireAction(confirm, function () {
      var value = typeof reason.value === "string" ? reason.value.trim() : "";
      if (value === "") {
        // RejectionSchema.reason 은 z.string().min(1)(contracts.ts:69-72) — 빈 사유는 서버가
        // 거부하므로 요청 자체를 보내지 않고 폼에서 막는다(구 prompt 경로와 같은 규칙).
        error.textContent = _MSG_REJECT_REASON_REQUIRED;
        error.setAttribute("data-bdc-visible", "true");
        return;
      }
      _closeRejectForm(panel);
      void _runAction(panel, "reject", value);
    });
    actions.appendChild(confirm);

    var back = document.createElement("button");
    back.type = "button";
    back.className = "ui-btn bdc-form-btn bdc-reject-back";
    back.setAttribute("data-bdc-reject", "back");
    back.textContent = _REJECT_BACK_LABEL;
    _wireAction(back, function () { _closeRejectForm(panel); });
    actions.appendChild(back);

    form.appendChild(actions);
    panel.appendChild(form);
  }

  function _closeRejectForm(panel: HTMLElement): void {
    var form = panel.querySelector(".bdc-reject-form");
    if (form && form.parentNode) form.parentNode.removeChild(form);
    var strip = panel.querySelector(".bdc-actions") as HTMLElement | null;
    if (strip) strip.hidden = false;
  }

  /**
   * 본문 폼은 서버를 부르지 않고 **패널 안에서 펼쳐진다** — 모달이 아닌 이유는 카드가 대화
   * 맥락 안에 있어야 하기 때문이다(phase-03 §5).
   *
   * `typeof` 가드는 chat.ts 가 이 카드 모듈을 부를 때 쓴 것과 같은 관례다(chat.ts:825-828) —
   * 폼 스크립트가 없어도 카드는 그대로 동작하고, 컴파일 산출물을 new Function 으로 실행하는
   * 테스트 하네스가 카드만 로드해도 ReferenceError 가 나지 않는다.
   *
   * 초기값은 방금 그린 레코드에서 온다. 폼이 스스로 GET /:id 를 다시 하면 같은 봉투를 두 번
   * 받고, 그 사이 값이 갈라지면 사용자가 보고 있는 카드와 폼이 다른 문서를 주장한다.
   */
  function _openBodyForm(panel: HTMLElement): void {
    if (typeof BusinessDocumentBodyForm === "undefined" || !BusinessDocumentBodyForm) return;
    var doc = _documentByPanel.get(panel);
    // 도달성: edit 는 조회 성공 후에만 그려진다(_renderStatusOnly 는 액션을 붙이지 않는다).
    if (!doc) return;

    // 폼이 열려 있는 동안 패널 카운터를 숨긴다(P4) — 저장할 때마다 폼의 미입력 목록은 갱신되는데
    // 패널 카운터는 재렌더 없이는 낡은 수를 주장하기 때문이다. 닫힐 때 onSaved/onClosed 가
    // 새 값으로 다시 그리거나 되살린다.
    var progress = panel.querySelector(".bdc-progress") as HTMLElement | null;
    if (progress) progress.hidden = true;

    // 액션 스트립도 숨긴다(사용자 결정 2026-08-30) — 폼이 펼쳐진 동안의 행동(저장·승인 요청·
    // 닫기)은 폼 하단 우측에 모인다. 두 곳에 버튼이 있으면 어느 쪽이 진짜인지 갈라진다.
    var strip = panel.querySelector(".bdc-actions") as HTMLElement | null;
    if (strip) strip.hidden = true;

    BusinessDocumentBodyForm.open({
      panel: panel,
      documentId: _str(panel.getAttribute("data-business-document-id")),
      documentType: _str(doc.documentType),
      body: doc.body,
      // 저장 성공(완주) → 응답의 document 로 카드 전체를 다시 그린다. 낙관적 갱신을 하지 않는
      // 것은 액션 경로와 같은 이유다 — 프런트가 다음 상태를 추측하면 판정자가 둘이 된다.
      // missingCount 는 PATCH 응답의 completeness 에서 온다 — 추가 왕복 없이 카운터·액센트가
      // 방금 저장된 사실을 따라간다(P3·P4).
      onSaved: function (next: Record<string, unknown>, missingCount?: number | null) {
        _renderDocument(panel, next, typeof missingCount === "number" ? missingCount : undefined);
      },
      // 닫기(저장 없이 또는 중간 저장 후) — 마지막으로 안 완성도가 있으면 그 값으로 다시
      // 그리고, 없으면 숨겼던 카운터·스트립만 되살린다.
      onClosed: function (missingCount?: number | null) {
        var hiddenProgress = panel.querySelector(".bdc-progress") as HTMLElement | null;
        if (hiddenProgress) hiddenProgress.hidden = false;
        var hiddenStrip = panel.querySelector(".bdc-actions") as HTMLElement | null;
        if (hiddenStrip) hiddenStrip.hidden = false;
        var current = _documentByPanel.get(panel);
        if (current && typeof missingCount === "number") _renderDocument(panel, current, missingCount);
      },
      // 폼 하단의 [승인 요청](사용자 결정 2026-08-30) — 폼이 저장을 끝내고 스스로 닫힌 뒤
      // 부른다. 전이 실행은 카드 액션 경로 하나다(판정자·오류 처리 공유).
      onSubmitRequest: function () {
        var hiddenStrip = panel.querySelector(".bdc-actions") as HTMLElement | null;
        if (hiddenStrip) hiddenStrip.hidden = false;
        void _runAction(panel, "submit", "");
      },
      // 코드 -> 문구 표는 이 모듈이 소유한다(ERROR_STATUS 유도). 폼이 사본을 뜨면 두 표가 된다.
      errorCopy: _errorCopy,
    });
  }

  // 발행 확인 모달용 금액 표기 — 조 단위 구분자만. 산식의 SSOT 는 서버(amount-rules)이고
  // 여기서는 저장값을 표시만 한다.
  function _formatAmount(value: unknown): string {
    if (typeof value !== "number" || !isFinite(value)) return "";
    var sign = value < 0 ? "-" : "";
    return sign + String(Math.trunc(Math.abs(value))).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  /** "'제목' · 상대방 아무개 · 총액 13,200,000원" — 확인 모달에 실을 발행 대상 요약. */
  function _publishSummary(doc: Record<string, unknown> | null): string {
    if (!doc) return "";
    var parts: string[] = [];
    var title = _str(doc.title);
    if (title !== "") parts.push("'" + title + "'");
    var counterparty = _isRecord(doc.counterparty) ? _str(doc.counterparty.displayName) : "";
    if (counterparty !== "") parts.push("상대방 " + counterparty);
    var body = _isRecord(doc.body) ? doc.body : {};
    var isContract = _str(doc.documentType) === "contract";
    var amount = _formatAmount(body[isContract ? "contractAmount" : "totalAmount"]);
    if (amount !== "") parts.push((isContract ? "계약 금액 " : "총액 ") + amount + "원");
    return parts.join(" · ");
  }

  /** 비가역 전이(publish·void) 공통 확인 모달. 문구만 갈라지고 확인 계약은 하나다. */
  function _confirmIrreversible(title: string, body: string, warning: string, confirmLabel: string,
    onConfirm: () => void): void {
    if (typeof showConfirmModal !== "function") {
      // index.html 로드 순서상 도달하지 않는다. 확인 수단이 없을 때 되돌릴 수 없는 전이를
      // 그냥 실행하느니 아무 일도 일어나지 않는 편이 낫다.
      console.error("[business-document-card] 확인 모달 유틸이 없어 비가역 전이를 진행하지 않는다");
      return;
    }
    showConfirmModal({
      title: title,
      body: body,
      warning: warning,
      confirmLabel: confirmLabel,
      onConfirm: onConfirm,
    });
  }

  async function _runAction(panel: HTMLElement, action: string, reason: string): Promise<void> {
    var documentId = _str(panel.getAttribute("data-business-document-id"));
    if (documentId === "") return;

    var sessionId = _currentSessionId();
    _setActionsBusy(panel, true);

    // 발행은 새 아티팩트 카드를 방 하단에 만든다 — 그 카드가 도착하면 스크롤이 따라가도록
    // 창을 연다(P6). 카드는 POST 응답보다 먼저 도착할 수 있으므로 요청 전에 연다.
    // 미리보기는 카드를 만들지 않으므로(D-QUIET-PREVIEW) 따라갈 것이 없다.
    if (action === "publish") _armFollow(documentId);

    var result = await _sendAction(action, documentId, reason);

    // await 이후 세션 재확인(work-inline-cards.ts:355 관용) — 요청 진행 중 방을 나갔으면
    // 지금 화면에 있는 것은 다른 방의 DOM 이다. 아무것도 건드리지 않는다.
    if (_currentSessionId() !== sessionId) {
      if (action === "publish") _disarmFollow();
      return;
    }

    if (!result.ok) {
      if (action === "publish") _disarmFollow();
      _setActionsBusy(panel, false);
      await _handleActionError(panel, documentId, result.code);
      return;
    }

    // 조용한 미리보기(D-QUIET-PREVIEW, 사용자 승인 2026-08-30) — 서버는 카드를 만들지 않았고
    // 문서 상태도 그대로다. 사용자가 기대한 것은 카드가 아니라 **문서 그 자체**이므로 PDF 를
    // 새 탭으로 곧장 연다(A4 검토는 브라우저 뷰어가 최적 표면). 재조회할 것이 없다.
    if (action === "preview" && _isRecord(result.payload.outputs)
      && typeof result.payload.outputs.pdfHref === "string" && result.payload.outputs.pdfHref !== "") {
      _setActionsBusy(panel, false);
      _openExternal(result.payload.outputs.pdfHref);
      return;
    }

    if (_isRenderAction(action)) {
      // 캡처한 패널에 그리면 안 된다. 발행 성공과 거의 동시에 새 아티팩트 카드가 도착하면
      // _pruneDuplicatePanels 의 removeChild 가 이 패널을 DOM 에서 떼어내고, _renderPanel 의
      // 부모 가드가 그리기를 통째로 버린다 — 카드는 "승인됨 / [발행]" 인 채로 남아 이미 끝난
      // 비가역 작업을 다시 권한다. documentId 로 **생존 패널**을 다시 찾는다.
      //
      // 같은 파일 안에서는 줄 번호로 인용하지 않는다. 이 주석을 넣은 diff 하나로 위쪽 줄이
      // 밀려 인용이 곧바로 무효가 되는 것을 이 phase 에서 실제로 겪었다 — 심볼 이름으로 건다.
      var host = _chatMessagesEl();
      var live = host ? _findDocumentPanel(host, documentId) : null;
      await _load((live as HTMLElement) || panel, documentId);
      return;
    }

    if (_isRecord(result.payload.document)) {
      _renderDocument(panel, result.payload.document);
      return;
    }

    // 계약상 도달하지 않는다(세 라우터가 전부 document 를 싣는다). 추측으로 그리지 않고
    // 서버에 다시 묻는다.
    await _load(panel, documentId);
  }

  async function _handleActionError(panel: HTMLElement, documentId: string, code: string): Promise<void> {
    if (code === "render_preconditions_unmet") {
      await _explainRenderPrecondition(panel, documentId);
      return;
    }
    if (code === "invalid_transition") {
      await _load(panel, documentId);
      _showActionError(panel, _errorCopy(code), []);
      return;
    }
    if (code === "document_not_found") {
      // 카드를 비활성으로 접는다 — 없는 문서에 액션을 계속 내걸지 않는다.
      _renderStatusOnly(panel, _PILL_ERROR, _errorCopy(code));
      return;
    }
    _showActionError(panel, _errorCopy(code), []);
  }

  /**
   * render_preconditions_unmet(409)는 **서로 다른 사실 다섯 개**가 접히는 코드다: 본문 미입력 ·
   * 이미 발행됨(publish-ops.ts:238) · 폐기됨(:243) · 발행 법인 미설정
   * (business-document-model.ts:243-245 가 rethrowAsPrecondition 으로 감싼다) · 저장 금액 불일치.
   *
   * 그래서 무조건 "항목을 채우세요" 로 매핑하면 낡은 카드에서 **빈 목록**을 그리는 침묵 실패가
   * 된다. missing 이 비어 있으면 그때부터는 "무엇이 비었나" 가 아니라 "상태가 바뀌었나" 다.
   */
  async function _explainRenderPrecondition(panel: HTMLElement, documentId: string): Promise<void> {
    var assessed = await _get(_apiAction(documentId, "completeness"));
    var completeness = assessed.ok && _isRecord(assessed.payload.completeness) ? assessed.payload.completeness : null;
    var rawMissing = completeness && Array.isArray(completeness.missing) ? completeness.missing : [];
    var missing = rawMissing.filter(function (item: unknown) { return typeof item === "string" && item !== ""; });

    if (missing.length > 0) {
      _showActionError(panel, _MSG_INCOMPLETE, missing.map(function (item: unknown) { return _missingLabel(String(item)); }));
      return;
    }

    var fresh = await _get(_apiDocument(documentId));
    var doc = fresh.ok && _isRecord(fresh.payload.document) ? fresh.payload.document : null;
    if (!doc) {
      _showActionError(panel, _errorCopy("render_preconditions_unmet"), []);
      return;
    }

    var state = _str(doc.state);
    var text = state === "rendered" ? _MSG_ALREADY_RENDERED
      : state === "void" ? _MSG_VOIDED
      : _MSG_ISSUER_OR_AMOUNT;

    // 재렌더가 먼저다 — _renderPanel 이 패널을 비우므로 순서가 뒤집히면 오류가 지워진다.
    _renderDocument(panel, doc);
    _showActionError(panel, text, []);
  }

  // ===== 인테이크 (BD-C1 Phase 4) =====
  // `/견적서` 한 줄에서 초안 레코드까지. 서버는 한 줄도 바뀌지 않는다 — 플러그인 execute 가 이미
  // 돌려주는 next 페이로드(index.ts:53-81)의 **첫 소비자**가 여기다.

  function _findDocumentPanel(host: Element, documentId: string): Element | null {
    var panels = host.querySelectorAll(".bdc-panel");
    for (var index = 0; index < panels.length; index += 1) {
      if (_str(panels[index].getAttribute("data-business-document-id")) === documentId) return panels[index];
    }
    return null;
  }

  /**
   * 아티팩트 없이 문서 레코드만 가리키는 독립 카드. 스냅샷 속성(data-bdc-draft/number/path)을
   * 붙이지 않는다 — 이 카드는 어떤 PDF 바이트도 가리키지 않기 때문이다. 그래서 rendered 상태에서
   * _canDownload 가 빈 path 를 보고 다운로드를 걷어내고 왜 없는지 말한다(§5).
   *
   * 클래스에 .bdc-panel 을 그대로 실어 문서당 카드 1개 불변식(_pruneDuplicatePanels)의 대상이
   * 되게 한다 — 나중에 같은 문서의 아티팩트 카드가 도착하면 이 독립 카드가 통째로 은퇴한다.
   */
  function _insertDocumentCard(doc: Record<string, unknown>, follow = true): void {
    var host = _chatMessagesEl();
    var documentId = _str(doc.id);
    if (!host || documentId === "") return;
    // 멱등 — 유일한 중복 판정자는 "DOM 에 이미 그 documentId 카드가 있는가" 다(§5).
    if (_findDocumentPanel(host, documentId)) return;
    var panel = document.createElement("div");
    panel.className = "bdc-panel bdc-standalone";
    panel.setAttribute("data-business-document-id", documentId);
    var anchorSeq = _standaloneAnchorSeq(doc);
    if (anchorSeq !== null) panel.setAttribute("data-anchor-seq", String(anchorSeq));
    host.appendChild(panel);
    if (anchorSeq !== null) {
      _observePlacement(host);
      _syncPlacement(host);
    }
    // 먼저 즉시 그리고(레코드는 손에 있다), 작성 단계면 완성도를 뒤따라 얹는다(P3) —
    // 인테이크 직후가 카드가 가장 위험한 순간(빈 본문 + 비가역 여정 입구)이기 때문이다.
    _renderDocument(panel, doc);
    if (follow) host.scrollTop = host.scrollHeight;
    void _renderWithCompleteness(panel, documentId, doc);
  }

  // ===== 타임라인 위치 (아티팩트 없는 독립 카드) =====
  // 아티팩트가 하나도 없는 문서의 독립 카드는 초안을 만든 자리 — 서버가 생성 시점에 잰 방 세션 로그의 마지막 seq
  // (record.anchorSeq, document-ops.ts resolveDraftAnchorSeq) — 바로 뒤, 다음 메시지 앞에 놓인다. 대화 끝에 쌓이지 않는다.
  // 아티팩트가 있는 문서는 이 규칙 밖이다: 로그에 남은 아티팩트 카드가 이미 제자리에 있고, 독립 카드의 위치를 바꾸면
  // 문서당 카드 1개 불변식(_pruneDuplicatePanels — DOM 상 마지막 패널 생존)의 판정이 달라진다.
  // 배치 규칙은 WorkInlineCards 의 _syncPlacement(work-inline-cards.ts)를 그대로 미러한다 — 같은 seq 좌표, 같은 관찰
  // 방식, 같은 "다음 메시지 형제만으로 제자리 판정"(관찰 루프 방지), 같은 숨김 주차(첫 자식 금지), 같은 스크롤 보존.
  // 공유 헬퍼로 빼지 않는다: 두 모듈은 classic script 이고, 테스트 하네스가 이 파일 하나만 new Function 으로 실행한다.
  // 이 경로의 DOM 접근은 getAttribute·className 으로만 한다 — 그 하네스의 가짜 요소에는 classList·dataset 이 없다.

  function _standaloneAnchorSeq(doc: Record<string, unknown>): number | null {
    var artifactIds = doc.artifactIds;
    if (Array.isArray(artifactIds) && artifactIds.length > 0) return null;
    var seq = doc.anchorSeq;
    // -1 = 메시지가 하나도 없던 방에서 만든 초안(첫 메시지 앞).
    return typeof seq === "number" && Number.isInteger(seq) && seq >= -1 ? seq : null;
  }

  function _hasClass(node: Element, name: string): boolean {
    return (" " + _str(node.className) + " ").indexOf(" " + name + " ") >= 0;
  }

  var _observedHosts = new WeakSet<HTMLElement>();

  function _observePlacement(host: HTMLElement): void {
    if (_observedHosts.has(host) || typeof MutationObserver === "undefined") return;
    _observedHosts.add(host);
    new MutationObserver(function () { _syncPlacement(host); }).observe(host, { childList: true });
  }

  function _nodeSeq(node: Element): number | null {
    if (!node || typeof node.getAttribute !== "function" || !_hasClass(node, "message")) return null;
    var sessionId = node.getAttribute("data-session-id");
    if (sessionId && sessionId !== _currentSessionId()) return null;
    var raw = node.getAttribute("data-msg-seq");
    var seq = raw === null ? NaN : Number(raw);
    return Number.isInteger(seq) && seq >= 0 ? seq : null;
  }

  function _syncPlacement(host: HTMLElement): void {
    var children = Array.from(host.children);
    var cards = children.filter(function (node): node is HTMLElement {
      return _hasClass(node, "bdc-standalone") && node.getAttribute("data-anchor-seq") !== null;
    });
    if (!cards.length) return;
    var messages: Array<{ node: Element; seq: number }> = [];
    children.forEach(function (node) {
      var seq = _nodeSeq(node);
      if (seq !== null) messages.push({ node: node, seq: seq });
    });
    if (!messages.length) return;
    var oldest = Math.min.apply(null, messages.map(function (entry) { return entry.seq; }));
    var hasOlder = typeof messageListState !== "undefined" && messageListState.hasMore === true;
    // 스크롤 보정 기준은 옮기기 직전에 한 번만 잰다(제자리면 레이아웃을 읽지 않는다). 옮겨질 카드는 기준에서 뺀다.
    var view: { follow: boolean; top: number; anchor?: HTMLElement; anchorTop?: number } | null = null;
    var beforeMove = function () {
      if (view) return;
      var bounds = host.getBoundingClientRect();
      var anchorEl = children.find(function (node): node is HTMLElement {
        return !(node as HTMLElement).hidden && !_hasClass(node, "bdc-standalone") && node.getBoundingClientRect().bottom > bounds.top;
      });
      view = { follow: host.scrollHeight - host.scrollTop - host.clientHeight < 4, top: host.scrollTop, anchor: anchorEl, anchorTop: anchorEl ? anchorEl.offsetTop : undefined };
    };
    cards.forEach(function (card) {
      var anchor = Number(card.getAttribute("data-anchor-seq"));
      // 만든 자리가 아직 불러오지 않은 과거면 맨 끝에 숨겨 둔다. 맨 위에 두면 숨은 카드가 첫 자식이 되어 과거 불러오기
      // (prependHistory)의 스크롤 보정 기준(firstElementChild.offsetTop)이 0 으로 굳는다.
      // anchor -1(첫 메시지 앞)은 seq 0 이 이미 보이면 더 오래된 메시지가 있을 수 없으므로 숨기지 않는다.
      var anchorUnloaded = anchor < 0 ? oldest > 0 : anchor < oldest;
      if (anchorUnloaded && hasOlder) {
        if (!card.hidden) {
          beforeMove();
          host.appendChild(card);
          card.hidden = true;
        }
        return;
      }
      // 제자리 = 카드 바로 다음 메시지가 "anchor 보다 뒤인 첫 메시지"(없으면 카드 뒤에 메시지가 없음).
      var target = messages.filter(function (entry) { return entry.seq > anchor; })[0];
      var nextEl: Element | null = target ? target.node : null;
      var following = card.nextElementSibling;
      while (following && _nodeSeq(following) === null) following = following.nextElementSibling;
      var wasHidden = card.hidden;
      if (following === nextEl && !wasHidden) return;
      beforeMove();
      card.hidden = false;
      if (following === nextEl) return;
      if (nextEl) {
        var divider = nextEl.previousElementSibling;
        host.insertBefore(card, divider && _hasClass(divider, "chat-date-divider") ? divider : nextEl);
      } else {
        host.appendChild(card);
      }
    });
    var settled = view as { follow: boolean; top: number; anchor?: HTMLElement; anchorTop?: number } | null;
    if (!settled) return;
    if (settled.follow) host.scrollTop = host.scrollHeight;
    else if (settled.anchor && settled.anchor.parentElement === host && settled.anchorTop !== undefined) host.scrollTop = settled.top + settled.anchor.offsetTop - settled.anchorTop;
  }

  function _hasRoomLocator(sourceRefs: unknown, roomId: string): boolean {
    if (!Array.isArray(sourceRefs)) return false;
    for (var index = 0; index < sourceRefs.length; index += 1) {
      var ref: unknown = sourceRefs[index];
      if (_isRecord(ref) && _str(ref.locator) === roomId) return true;
    }
    return false;
  }

  /**
   * 방 재진입 복원(§5). 초안 카드는 세션 로그에 영속되지 않으므로 — POST / 는 아티팩트도 로그
   * 엔트리도 만들지 않고, 카드가 리로드에서 살아남는 유일한 경로는 renderHistoryMessage 의
   * msg.artifact 분기다 — 서버 진실(GET /)에서 다시 합성한다. WorkInlineCards.restore
   * (work-inline-cards.ts:458)의 형태를 그대로 미러한다.
   *
   * 히스토리 렌더 **뒤에** 돈다(ws-handlers.ts 가 그 순서로 부른다) — 중복 판정이 DOM 이므로
   * 아티팩트 카드가 먼저 그려져 있어야 같은 문서의 카드가 둘 생기지 않는다.
   *
   * 실패는 삼킨다(work-inline-cards.ts:476 미러) — 복원 실패가 채팅 렌더를 막으면 안 된다.
   */
  async function restoreForSession(sessionId?: string | null): Promise<void> {
    var host = _chatMessagesEl();
    if (!host || !sessionId) return;
    var roomId = _currentRoomId();
    // 방 밖 1:1 세션에는 방 locator 가 없어 그 세션의 문서를 특정할 수 없다 — 문서함에서만
    // 다시 찾을 수 있다(§6, 명시된 accepted risk).
    if (roomId === null || !_ROOM_ID_RE.test(roomId)) return;
    try {
      // 평면 무필터 목록이다(사용자 확정 결정 "평면 가시"). 서버 필터는 그 결정을 바꾸는 일이라
      // 하지 않는다 — 방 필터는 여기 클라이언트 투영이다.
      var result = await _get("/api/business-documents/");
      // await 이후 세션 재확인(work-inline-cards.ts:465 관용) — 목록 조회 중 방을 옮겼으면
      // 지금 화면에 있는 것은 다른 방의 DOM 이다.
      if (_currentSessionId() !== sessionId) return;
      var raw: unknown = result.payload.documents;
      if (!result.ok || !Array.isArray(raw)) return;

      var documents: Record<string, unknown>[] = [];
      for (var index = 0; index < raw.length; index += 1) {
        var doc: unknown = raw[index];
        if (!_isRecord(doc)) continue;
        // 폐기 문서는 그리지 않는다. 폐기 전이를 실행하는 엔드포인트가 레포에 0개라 도달하지
        // 않지만, 상태가 생기는 날 조용히 되살아나지 않게 한다.
        if (_str(doc.state) === "void") continue;
        if (!_hasRoomLocator(doc.sourceRefs, roomId)) continue;
        // 아티팩트 없는 문서는 만든 자리(anchorSeq)가 있을 때만 그린다 — 자리를 모르는 옛 초안(anchorSeq 도입 전)을
        // 대화 끝에 쌓지 않는다. 그 문서들은 문서함에 그대로 있다. 아티팩트가 있는 문서는 종전 그대로다.
        var hasArtifacts = Array.isArray(doc.artifactIds) && doc.artifactIds.length > 0;
        if (!hasArtifacts && _standaloneAnchorSeq(doc) === null) continue;
        documents.push(doc);
      }
      documents.sort(function (a, b) { return _str(a.createdAt).localeCompare(_str(b.createdAt)); });
      var follow = host.scrollHeight - host.scrollTop - host.clientHeight < 4;
      documents.forEach(function (record) { _insertDocumentCard(record, follow); });
    } catch (error) {
      console.error("[business-document-card] restore 실패:", error instanceof Error ? error.message : String(error));
    }
  }

  function _createIntakeContainer(): HTMLElement | null {
    var host = _chatMessagesEl();
    if (!host) return null;
    var container = document.createElement("div");
    container.className = "bdc-intake";
    host.appendChild(container);
    host.scrollTop = host.scrollHeight;
    return container;
  }

  function _appendIntakeMessage(container: HTMLElement, message: string): void {
    if (message === "") return;
    // 서버가 준 안내 문장이다 — 프런트가 문서 종류별 문구 표를 두 번째로 갖지 않는다.
    var line = document.createElement("div");
    line.className = "bdc-intake-message";
    line.textContent = message;
    container.appendChild(line);
  }

  function _appendIntakeField(parent: HTMLElement, label: string, placeholder: string, value: string): HTMLInputElement {
    var field = document.createElement("div");
    field.className = "bdc-field";
    _appendSpan(field, "bdc-field-label", label);
    var input = document.createElement("input");
    input.className = "bdc-field-input";
    input.type = "text";
    input.value = value;
    input.setAttribute("placeholder", placeholder);
    field.appendChild(input);
    parent.appendChild(field);
    return input;
  }

  function _showIntakeError(state: BusinessDocumentIntakeState, text: string): void {
    state.error.textContent = text;
    // 빈 상자를 그리지 않는다 — 문구가 있을 때만 보인다(폼 오류 상자와 같은 규칙).
    state.error.setAttribute("data-bdc-visible", text === "" ? "false" : "true");
  }

  /**
   * 방 안이면 locator 는 방 id 다. 방 밖(에이전트 1:1 세션)이면 세션 id 를 싣고 sourceType 은
   * 그대로 "chat" 이다 — collectRoomLocators 가 ROOM_ID_RE 로 거르므로 세션 id 가 방 locator 로
   * 오인되지 않는다(document-ops.ts:111-121).
   *
   * **이것이 방 스코프 게이트를 켜는 스위치다.** 방 locator 가 실려야
   * resolveVisibleRoomForDocument 가 동작해, 그 방이 보이는 액터만 본문을 고칠 수 있게 된다.
   */
  function _intakeSourceRef(): Record<string, unknown> | null {
    var roomId = _currentRoomId();
    var locator = roomId !== null && _ROOM_ID_RE.test(roomId) ? roomId : _currentSessionId();
    if (locator === null || locator === "") return null;
    return { id: "chat:" + locator, sourceType: "chat", locator: locator };
  }

  async function _submitIntake(state: BusinessDocumentIntakeState): Promise<void> {
    var title = state.titleInput.value.trim();
    var counterparty = state.counterpartyInput.value.trim();
    if (title === "") { _showIntakeError(state, _MSG_INTAKE_TITLE_REQUIRED); return; }
    if (counterparty === "") { _showIntakeError(state, _MSG_INTAKE_COUNTERPARTY_REQUIRED); return; }
    var sourceRef = _intakeSourceRef();
    if (!sourceRef) { _showIntakeError(state, _MSG_INTAKE_NO_SOURCE); return; }

    var sessionId = _currentSessionId();
    _showIntakeError(state, "");
    state.submit.disabled = true;

    // 서버가 채우는 필드(id·state·templateVersion·createdAt·createdBy)는 보내지 않는다 —
    // buildDraftDocumentRecord(document-ops.ts:60-83)가 조립한다. 본문(body)도 이 경로로 받지
    // 않는다: 본문 입력은 PATCH /:id/body 소유다.
    var result = await _post("/api/business-documents/", {
      documentType: state.documentType,
      title: title,
      issuerEntityId: BUSINESS_DOCUMENT_ISSUER_ENTITY_ID,
      counterparty: { displayName: counterparty },
      sourceRefs: [sourceRef],
    });

    // await 이후 세션 재확인 — 액션 경로(_runAction)와 같은 이유다.
    if (_currentSessionId() !== sessionId) return;

    if (!result.ok) {
      state.submit.disabled = false;
      // issuer_not_ready(503)가 실제로 도달하는 유일한 경로가 여기다 — 코드 -> 문구 표는
      // 이 모듈이 소유하므로 사본을 뜨지 않고 그대로 쓴다.
      _showIntakeError(state, _errorCopy(result.code));
      return;
    }

    var doc = _isRecord(result.payload.document) ? result.payload.document : null;
    if (!doc) {
      // 계약상 도달하지 않는다(routes-draft.ts:68 이 201 에 document 를 싣는다).
      state.submit.disabled = false;
      _showIntakeError(state, _errorCopy("internal_error"));
      return;
    }

    // 폼은 제 일을 끝냈다 — 같은 자리를 문서 카드가 잇는다.
    if (state.container.parentNode) state.container.parentNode.removeChild(state.container);
    _insertDocumentCard(doc);
  }

  function _renderIntakeForm(container: HTMLElement, documentType: string, title: string, message: string): void {
    _appendIntakeMessage(container, message !== "" ? message : _MSG_INTAKE_PROMPT);

    var fields = document.createElement("div");
    fields.className = "bdc-form-fields";
    // 제목은 서버가 준 next.title 로 프리필한다 — `/견적서 웹사이트 제작` 의 인자가 그것이다.
    var titleInput = _appendIntakeField(fields, _INTAKE_TITLE_LABEL, _INTAKE_TITLE_PLACEHOLDER, title);
    var counterpartyInput = _appendIntakeField(fields, _INTAKE_COUNTERPARTY_LABEL, _INTAKE_COUNTERPARTY_PLACEHOLDER, "");
    container.appendChild(fields);

    var error = document.createElement("div");
    error.className = "bdc-form-error";
    error.setAttribute("data-bdc-visible", "false");
    container.appendChild(error);

    var actions = document.createElement("div");
    actions.className = "bdc-form-actions";
    var submit = document.createElement("button");
    submit.type = "button";
    submit.className = "ui-btn bdc-form-btn bdc-form-save";
    submit.setAttribute("data-bdc-action", "create-draft");
    submit.textContent = _INTAKE_SUBMIT_LABEL;
    actions.appendChild(submit);
    container.appendChild(actions);

    var state: BusinessDocumentIntakeState = {
      container: container,
      documentType: documentType,
      titleInput: titleInput,
      counterpartyInput: counterpartyInput,
      error: error,
      submit: submit,
    };
    _wireAction(submit, function () { void _submitIntake(state); });
  }

  function _openIntake(documentType: string, title: string, message: string): boolean {
    // 종류를 모르면 폼을 열지 않는다 — 열어도 제출이 스키마 밖 documentType 으로 400 이다.
    // false 를 돌려주면 ws-handlers 가 서버 안내 문장을 그대로 보여준다.
    if (documentType === "") return false;
    var container = _createIntakeContainer();
    if (!container) return false;
    _renderIntakeForm(container, documentType, title, message);
    return true;
  }

  function _appendTypeChoice(strip: HTMLElement, container: HTMLElement, choice: { documentType: string; label: string }): void {
    var button = document.createElement("button");
    button.type = "button";
    button.className = "ui-btn bdc-form-btn";
    button.setAttribute("data-bdc-document-type", choice.documentType);
    button.textContent = choice.label;
    _wireAction(button, function () {
      // 서버 왕복 없이 같은 자리에서 폼으로 전환한다 — 종류 선택은 클라이언트 상태다.
      container.innerHTML = "";
      _renderIntakeForm(container, choice.documentType, "", choice.label + " 초안을 만들게요.");
    });
    strip.appendChild(button);
  }

  /**
   * 종류 선택은 맨 `/document` 로 들어왔을 때만 나온다(index.ts:57-67). 별칭
   * (`/견적서`·`/quote`·`/contract`)은 aliasDefaults 로 documentType 이 이미 판정되어
   * 곧바로 draft-intake 다 — 별칭 경로에는 이 단계가 없다.
   *
   * 버튼은 next.options 에서 **유도한다**. 문서 종류 표를 프런트가 하드코딩하면
   * BUSINESS_DOCUMENT_TYPES 가 늘 때 조용히 한쪽만 갱신된다.
   */
  function _openTypePicker(options: unknown, message: string): boolean {
    if (!Array.isArray(options)) return false;
    var choices: { documentType: string; label: string }[] = [];
    for (var index = 0; index < options.length; index += 1) {
      var option: unknown = options[index];
      if (!_isRecord(option)) continue;
      var documentType = _str(option.documentType);
      if (documentType === "") continue;
      choices.push({ documentType: documentType, label: _str(option.label) || documentType });
    }
    if (choices.length === 0) return false;

    var container = _createIntakeContainer();
    if (!container) return false;
    _appendIntakeMessage(container, message);

    var strip = document.createElement("div");
    strip.className = "bdc-intake-options";
    container.appendChild(strip);
    // 클로저가 아니라 루프다 — 콜백 안에서는 container 의 null 좁힘이 풀린다.
    for (var pick = 0; pick < choices.length; pick += 1) {
      _appendTypeChoice(strip, container, choices[pick]);
    }
    return true;
  }

  /**
   * command-result 를 이 모듈이 처리했는지 boolean 으로 돌려준다. ws-handlers.ts 가 명령 별칭
   * 문자열 목록을 두 번째로 알지 않게 하기 위해서다 — 판정은 명령 문자열이 아니라 페이로드
   * 형태(next.kind)로 한다. 별칭이 늘어도 프런트는 따라갈 필요가 없다.
   *
   * data.data 는 없을 수 있다 — isValidSuccessfulCommandResultPayload(ws-payload-guard.ts:184-192)가
   * undefined/null 도 유효로 통과시킨다. 부재는 크래시가 아니라 false 반환으로 접는다.
   */
  function handleCommandResult(data: unknown): boolean {
    if (!_isRecord(data)) return false;
    var payload = _isRecord(data.data) ? data.data : null;
    if (!payload) return false;
    var next = _isRecord(payload.next) ? payload.next : null;
    if (!next) return false;
    var kind = _str(next.kind);
    if (kind === _INTAKE_KIND_DRAFT) return _openIntake(_str(next.documentType), _str(next.title), _str(next.message));
    if (kind === _INTAKE_KIND_CHOOSE) return _openTypePicker(next.options, _str(next.message));
    return false;
  }

  // ===== 진입점 =====

  function decorate(cardEl: HTMLElement, artifact: unknown): void {
    if (!cardEl || !_isRecord(artifact)) return;
    // display 가 아니라 kind 로 분기한다. display 값 집합(artifact-store.ts:30)은 z.enum 의
    // 입력이라 값 하나를 늘리면 서버 스키마 2곳 + 프런트 렌더러 맵 2개가 같이 열린다.
    // kind 는 z.string().min(1)(artifact-store.ts:34) 자유 분류자다.
    var documentType = _DOCUMENT_TYPE_BY_ARTIFACT_KIND[_str(artifact.kind)];
    if (!documentType) return;

    var payload = _isRecord(artifact.payload) ? artifact.payload : {};
    var documentId = _str(payload.documentId);
    if (documentId === "") return;

    // 멱등 — 같은 카드를 두 번 장식하지 않는다(히스토리 재렌더 + 라이브 이벤트 중복 도달).
    if (cardEl.querySelector(".bdc-panel")) return;

    var isDraft = payload.isDraft === true;
    var panel = document.createElement("div");
    panel.className = "bdc-panel";
    panel.setAttribute("data-business-document-id", documentId);
    if (isDraft) panel.setAttribute("data-bdc-draft", "true");
    // 발행 시점 스냅샷 사실 둘. 다운로드 게이팅이 조회 결과와 이 둘을 함께 봐야 판정된다(§5).
    panel.setAttribute("data-bdc-number", _str(payload.documentNumber));
    panel.setAttribute("data-bdc-path", _str(payload.path));
    cardEl.appendChild(panel);
    _renderStatusOnly(panel, _PILL_LOADING, "");

    // 조회는 스윕이 시작한다 — 이 카드가 DOM 상 마지막인지는 삽입이 끝나야 알 수 있다.
    _pendingPanels.push(panel);
    _scheduleSweep();
  }

  return {
    decorate: decorate,
    // 상태 -> 액션 판정을 전역으로 노출한다. phase-02 검증 게이트가 문자열 스캔이 아니라
    // **함수를 실제로 호출**해 표를 뽑아 서버 상수(BUSINESS_DOCUMENT_STATES)와 대조한다 —
    // 문자열 스캔은 Phase 1 이 이미 상태 라벨 6개를 이 파일에 심어 두어 공허하다.
    resolveActions: resolveDocumentCardActions,
    // 화해 대기값을 노출한다. 테스트가 이 값을 읽어 대기하므로 상수를 바꿔도 게이트가 따라온다 —
    // 테스트에 숫자 사본을 두면 두 번째 진실이 된다(phase-02 §2).
    reconcileDelayMs: _RECONCILE_DELAY_MS,
    // 발행본 판정의 **유일한** 소유자를 전역으로 올린다. canDownload 는 이 카드의 렌더 경로가
    // 부르는 바로 그 함수다 — 문서함(phase-04)이 술어를 복제하면 초안 바이트를 정식본으로
    // 내주는 갈림이 생기므로, 사본을 만들 이유 자체를 없앤다.
    publishedOutput: {
      resolve: _resolvePublishedOutput,
      canDownload: _canDownload,
    },
    // ws-handlers.ts 위임 세 줄이 부르는 두 진입점(phase-04 §1). handleCommandResult 는 자기가
    // 처리했는지를 boolean 으로 돌려주고, restoreForSession 은 방 재진입 복원을 돈다.
    handleCommandResult: handleCommandResult,
    restoreForSession: restoreForSession,
  };
})();
